import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, useSpring, useMotionValue, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitType from 'split-type';
import anime from 'animejs';
import {
    Zap, Leaf, Volume2, Cpu, ArrowRight, ChevronDown, ArrowUpRight,
    MapPin, Phone, Mail, Shield, Clock, Wrench, Sparkles,
    Package, Smartphone, BarChart3, CheckCircle2, Menu, X,
    Battery, Gauge, Wind, Award, Users, TrendingUp, Star, Ticket,
} from 'lucide-react';
import { useSmoothScroll } from './useSmoothScroll';
import { createBooking, createTicket } from '../../services/publicApi';

gsap.registerPlugin(ScrollTrigger);

/* ─────────────────────────────────────────────
   TOKENS
───────────────────────────────────────────── */
const C = {
    bg: '#07090f',
    bgAlt: '#0b0f1a',
    surface: '#0f1623',
    surfaceAlt: '#131c2e',
    border: 'rgba(255,255,255,0.06)',
    borderHov: 'rgba(255,255,255,0.11)',
    accent: '#4F8CFF',
    accentDim: 'rgba(79,140,255,0.09)',
    accentGlow: 'rgba(79,140,255,0.18)',
    text: '#E8EDF5',
    sub: '#8892A4',
    muted: '#3D4A5C',
    green: '#22C55E',
};

const F = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

const EXPO = [0.19, 1, 0.22, 1];
const SPRING = { type: 'spring', stiffness: 55, damping: 22 };

/* ─────────────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────────────── */
const GLOBAL_CSS = `
  @keyframes orb-a { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(24px,-16px) scale(1.03)} }
  @keyframes orb-b { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-18px,14px) scale(1.02)} }
  @keyframes orb-c { 0%,100%{transform:translate(0,0)} 50%{transform:translate(14px,-10px)} }
  @keyframes blink  { 0%,100%{opacity:.35} 50%{opacity:.9} }
  ::selection { background:rgba(79,140,255,.2); color:#E8EDF5; }
  *,*::before,*::after { box-sizing:border-box; }
  html { scroll-behavior:auto; }
  body { background:${C.bg}; color:${C.text}; font-family:${F}; -webkit-font-smoothing:antialiased; -moz-osx-font-smoothing:grayscale; }
  input::placeholder { color:${C.muted}; }
  select option { background:${C.surface}; color:${C.text}; }
  ::-webkit-scrollbar { width:4px; }
  ::-webkit-scrollbar-track { background:transparent; }
  ::-webkit-scrollbar-thumb { background:rgba(255,255,255,.07); border-radius:99px; }
  ::-webkit-scrollbar-thumb:hover { background:rgba(255,255,255,.12); }
`;

/* ─────────────────────────────────────────────
   AMBIENT BACKGROUND — 3 CSS orbs, dot grid only
───────────────────────────────────────────── */
const AmbientBg = () => (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        <div style={{
            position: 'absolute', top: '-10%', left: '5%', width: 640, height: 480, borderRadius: '50%',
            background: `radial-gradient(ellipse,rgba(79,140,255,.09),transparent 70%)`,
            filter: 'blur(70px)', animation: 'orb-a 26s ease-in-out infinite'
        }} />
        <div style={{
            position: 'absolute', top: '40%', left: '58%', width: 480, height: 480, borderRadius: '50%',
            background: `radial-gradient(ellipse,rgba(99,102,241,.07),transparent 70%)`,
            filter: 'blur(65px)', animation: 'orb-b 32s ease-in-out infinite'
        }} />
        <div style={{
            position: 'absolute', top: '60%', left: '-6%', width: 360, height: 360, borderRadius: '50%',
            background: `radial-gradient(ellipse,rgba(34,197,94,.05),transparent 70%)`,
            filter: 'blur(60px)', animation: 'orb-c 38s ease-in-out infinite'
        }} />
        {/* subtle dot grid */}
        <div style={{
            position: 'absolute', inset: 0, opacity: .022,
            backgroundImage: `radial-gradient(circle,rgba(255,255,255,.85) 1px,transparent 1px)`,
            backgroundSize: '48px 48px'
        }} />
    </div>
);

/* ─────────────────────────────────────────────
   REVEAL PRIMITIVES
───────────────────────────────────────────── */
const Reveal = ({ children, delay = 0, y = 16, style = {}, className = '' }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.18 });
    return (
        <motion.div ref={ref} className={className} style={style}
            initial={{ opacity: 0, y }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay, ease: EXPO }}>
            {children}
        </motion.div>
    );
};

const Stagger = ({ children, delay = 0, gap = 0.065, style = {}, className = '' }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.1 });
    return (
        <motion.div ref={ref} className={className} style={style}
            initial="hidden" animate={inView ? 'show' : 'hidden'}
            variants={{ hidden: {}, show: { transition: { staggerChildren: gap, delayChildren: delay } } }}>
            {children}
        </motion.div>
    );
};

const SI = ({ children, y = 18, style = {}, className = '' }) => (
    <motion.div className={className} style={style}
        variants={{ hidden: { opacity: 0, y }, show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: EXPO } } }}>
        {children}
    </motion.div>
);

/* SplitType word reveal */
const SplitReveal = ({ children, as: Tag = 'h2', delay = 0, stagger = 0.05, style = {} }) => {
    const ref = useRef(null);
    useLayoutEffect(() => {
        if (!ref.current) return;
        const split = new SplitType(ref.current, { types: 'words' });
        if (!split.words) return;
        split.words.forEach(w => { w.style.display = 'inline-block'; w.style.willChange = 'transform,opacity'; });
        const ctx = gsap.context(() => {
            gsap.set(split.words, { yPercent: 100, opacity: 0 });
            gsap.to(split.words, {
                yPercent: 0, opacity: 1, duration: 1.0, stagger, delay,
                ease: 'expo.out',
                scrollTrigger: { trigger: ref.current, start: 'top 88%', toggleActions: 'play none none none' },
            });
        }, ref);
        return () => { ctx.revert(); split.revert(); };
    }, [delay, stagger]);
    return <Tag ref={ref} style={{ overflow: 'hidden', display: 'block', ...style }}>{children}</Tag>;
};

/* ─────────────────────────────────────────────
   BUTTON — clean, restrained, no ripple
───────────────────────────────────────────── */
const Btn = ({ children, to, href, onClick, variant = 'primary', size = 'md', style: extra = {}, fullWidth = false }) => {
    const [hov, setHov] = useState(false);
    const pad = size === 'lg' ? '13px 26px' : size === 'sm' ? '7px 15px' : '10px 20px';
    const fs = size === 'lg' ? '0.875rem' : size === 'sm' ? '0.75rem' : '0.82rem';

    const base = {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
        padding: pad, borderRadius: 8, fontSize: fs, fontWeight: 500,
        letterSpacing: '-0.008em', cursor: 'pointer', textDecoration: 'none',
        border: 'none', fontFamily: F, whiteSpace: 'nowrap', position: 'relative',
        transition: 'background .18s, box-shadow .18s, transform .18s, border-color .18s, color .18s',
        width: fullWidth ? '100%' : undefined,
        transform: hov ? 'translateY(-1px)' : 'translateY(0)',
        ...extra,
    };

    const styles = {
        primary: {
            background: hov ? '#6ba3ff' : C.accent,
            color: '#fff',
            boxShadow: hov ? `0 6px 20px rgba(79,140,255,.28)` : `0 1px 8px rgba(79,140,255,.15)`,
        },
        ghost: {
            background: 'transparent',
            color: hov ? C.text : C.sub,
            border: `1px solid ${hov ? C.borderHov : C.border}`,
        },
        subtle: {
            background: hov ? C.surfaceAlt : C.surface,
            color: hov ? C.text : C.sub,
            border: `1px solid ${C.border}`,
        },
    };

    const Tag = to ? Link : href ? 'a' : 'button';
    const props = to ? { to } : href ? { href } : { onClick, type: 'button' };

    return (
        <Tag {...props} style={{ ...base, ...styles[variant] }}
            onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}>
            {children}
        </Tag>
    );
};

/* ─────────────────────────────────────────────
   SCOOTER SVG — side profile, minimal vector
───────────────────────────────────────────── */
const ScooterSVG = ({ color = C.accent, size = 240, id = 'sc' }) => (
    <svg viewBox="0 0 320 200" width={size} height={size * 0.625} fill="none" aria-hidden="true">
        <defs>
            <linearGradient id={`body-${id}`} x1="60" y1="60" x2="280" y2="160">
                <stop offset="0%" stopColor="#1e293b" />
                <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
            <radialGradient id={`wheel-${id}`} cx="50%" cy="50%">
                <stop offset="0%" stopColor="#1a2540" />
                <stop offset="100%" stopColor="#080C14" />
            </radialGradient>
            <linearGradient id={`trail-${id}`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={color} stopOpacity="0" />
                <stop offset="100%" stopColor={color} stopOpacity="0.9" />
            </linearGradient>
            <linearGradient id={`beam-${id}`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#fff" stopOpacity="0.85" />
                <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            <radialGradient id={`glow-${id}`} cx="50%" cy="50%">
                <stop offset="0%" stopColor={color} stopOpacity="0.5" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
            </radialGradient>
        </defs>

        {/* floor shadow */}
        <ellipse cx="160" cy="192" rx="130" ry="10" fill={`url(#glow-${id})`} opacity="0.35" />

        {/* rear wheel */}
        <circle cx="72" cy="158" r="36" fill={`url(#wheel-${id})`} stroke={`${color}50`} strokeWidth="1.5" />
        <circle cx="72" cy="158" r="24" fill="none" stroke={`${color}20`} strokeWidth="1" />
        <circle cx="72" cy="158" r="6" fill={color} />
        {[0, 45, 90, 135].map(a => (
            <line key={a}
                x1={72 + 6 * Math.cos(a * Math.PI / 180)} y1={158 + 6 * Math.sin(a * Math.PI / 180)}
                x2={72 + 24 * Math.cos(a * Math.PI / 180)} y2={158 + 24 * Math.sin(a * Math.PI / 180)}
                stroke={`${color}35`} strokeWidth="1.2" />
        ))}

        {/* front wheel */}
        <circle cx="252" cy="158" r="36" fill={`url(#wheel-${id})`} stroke={`${color}50`} strokeWidth="1.5" />
        <circle cx="252" cy="158" r="24" fill="none" stroke={`${color}20`} strokeWidth="1" />
        <circle cx="252" cy="158" r="6" fill={color} />
        {[0, 45, 90, 135].map(a => (
            <line key={a}
                x1={252 + 6 * Math.cos(a * Math.PI / 180)} y1={158 + 6 * Math.sin(a * Math.PI / 180)}
                x2={252 + 24 * Math.cos(a * Math.PI / 180)} y2={158 + 24 * Math.sin(a * Math.PI / 180)}
                stroke={`${color}35`} strokeWidth="1.2" />
        ))}

        {/* rear fork */}
        <path d="M72 122 L88 158" stroke={`${color}70`} strokeWidth="4" strokeLinecap="round" />

        {/* main body */}
        <path d="M88 118 C88 96 108 78 138 74 L210 70 C238 68 258 84 262 108 L266 128 C268 140 260 152 246 154 L96 156 C82 156 76 146 76 134 Z"
            fill={`url(#body-${id})`} stroke={`${color}45`} strokeWidth="1.2" />

        {/* body highlight */}
        <path d="M100 118 C100 104 116 96 140 94 L208 91 C230 90 244 100 246 114"
            stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" strokeLinecap="round" fill="none" />

        {/* seat */}
        <path d="M130 74 C148 60 200 58 218 70 L210 74 C194 64 150 66 138 78 Z"
            fill={`${color}25`} />
        <path d="M132 72 C150 60 200 58 218 70" stroke={`${color}50`} strokeWidth="1" strokeLinecap="round" fill="none" />

        {/* front fork */}
        <path d="M248 118 L252 158" stroke={`${color}80`} strokeWidth="5" strokeLinecap="round" />

        {/* handlebar stem */}
        <rect x="238" y="52" width="8" height="58" rx="4" fill={`${color}65`} />

        {/* handlebar */}
        <path d="M224 56 C232 48 252 48 262 56" stroke={color} strokeWidth="7" strokeLinecap="round" />
        <path d="M224 56 C232 48 252 48 262 56" stroke={color} strokeWidth="12" strokeLinecap="round" opacity="0.18" />

        {/* battery bar */}
        <rect x="148" y="106" width="60" height="18" rx="6" fill={`${color}14`} stroke={`${color}45`} strokeWidth="1" />
        <rect x="151" y="109" width="38" height="12" rx="4" fill={color} opacity="0.85" />
        <text x="170" y="120" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="700" fontFamily={F}>80%</text>

        {/* headlight */}
        <ellipse cx="268" cy="112" rx="14" ry="9" fill={color} opacity="0.9" />
        <ellipse cx="284" cy="112" rx="28" ry="8" fill={`url(#beam-${id})`} opacity="0.25" />

        {/* speed trails */}
        <path d="M18 108 L62 108" stroke={`url(#trail-${id})`} strokeWidth="2.5" strokeLinecap="round" />
        <path d="M8 122 L56 122" stroke={`url(#trail-${id})`} strokeWidth="1.8" strokeLinecap="round" opacity="0.6" />
        <path d="M14 136 L58 136" stroke={`url(#trail-${id})`} strokeWidth="1.2" strokeLinecap="round" opacity="0.35" />
    </svg>
);

/* ─────────────────────────────────────────────
   HERO SCOOTER — floating, minimal parallax
───────────────────────────────────────────── */
const HeroScooter = ({ mouseX, mouseY }) => {
    const rx = useSpring(useTransform(mouseY, [-300, 300], [2, -2]), { stiffness: 35, damping: 22 });
    const ry = useSpring(useTransform(mouseX, [-400, 400], [-4, 4]), { stiffness: 35, damping: 22 });

    return (
        <div style={{ position: 'relative', width: '100%', maxWidth: 520 }}>
            {/* ambient glow — very subtle */}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <motion.div
                    animate={{ scale: [1, 1.06, 1], opacity: [0.18, 0.28, 0.18] }}
                    transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        width: 380, height: 380, borderRadius: '50%',
                        background: `radial-gradient(circle,${C.accentGlow} 0%,transparent 70%)`,
                        filter: 'blur(55px)'
                    }} />
            </div>

            {/* floor reflection line */}
            <div style={{ position: 'absolute', bottom: '7%', left: '50%', transform: 'translateX(-50%)', width: '65%', pointerEvents: 'none' }}>
                <motion.div animate={{ opacity: [0.2, 0.45, 0.2], scaleX: [0.88, 1.04, 0.88] }}
                    transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ height: 1, background: `linear-gradient(90deg,transparent,${C.accent}60,transparent)` }} />
                <div style={{
                    height: 32, marginTop: -1,
                    background: `linear-gradient(to bottom,${C.accentDim},transparent)`,
                    filter: 'blur(6px)'
                }} />
            </div>

            {/* scooter with gentle tilt + float */}
            <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: 'preserve-3d', perspective: 1000 }}>
                <motion.div
                    animate={{ y: [0, -14, 0] }}
                    transition={{ duration: 6, repeat: Infinity, ease: [0.45, 0, 0.55, 1] }}
                    style={{ position: 'relative', zIndex: 2 }}>
                    <img
                        src="/Rizta.png"
                        alt="Ather Rizta Electric Scooter"
                        style={{
                            width: 500, height: 'auto',
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 16px 48px rgba(79,140,255,0.32)) drop-shadow(0 4px 20px rgba(0,0,0,0.55))',
                        }}
                    />
                </motion.div>
            </motion.div>

            {/* floating stat chips — minimal glass */}
            {[
                { pos: { top: 32, right: 8 }, icon: Battery, label: 'Range', value: '159 km', delay: 1.1, dur: 4.8 },
                { pos: { bottom: 90, left: 8 }, icon: Leaf, label: 'Emissions', value: '0 g CO₂', delay: 1.3, dur: 5.6 },
                { pos: { top: '44%', right: -4 }, icon: Gauge, label: 'Top Speed', value: '80 km/h', delay: 1.5, dur: 4.1 },
            ].map(({ pos, icon: Icon, label, value, delay, dur }, i) => (
                <motion.div key={i} style={{ position: 'absolute', ...pos }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay, ease: EXPO }}>
                    <motion.div
                        animate={{ y: [0, -8, 0] }}
                        transition={{ duration: dur, repeat: Infinity, ease: 'easeInOut' }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px',
                            borderRadius: 12, background: 'rgba(11,15,26,0.75)',
                            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                            border: `1px solid ${C.border}`,
                        }}>
                        <div style={{
                            width: 28, height: 28, borderRadius: 7, background: C.accentDim,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                            <Icon style={{ width: 13, height: 13, color: C.accent }} />
                        </div>
                        <div>
                            <p style={{ fontSize: '0.55rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0, fontWeight: 600 }}>{label}</p>
                            <p style={{ fontSize: '0.84rem', fontWeight: 600, color: C.text, margin: '1px 0 0', letterSpacing: '-0.01em' }}>{value}</p>
                        </div>
                    </motion.div>
                </motion.div>
            ))}
        </div>
    );
};

/* ─────────────────────────────────────────────
   NAVBAR
───────────────────────────────────────────── */
const NAV = [
    { label: 'Why Electric', href: '#why-electric' },
    { label: 'Our EVs', href: '#ev-collection' },
    { label: 'Operations', href: '#operations' },
    { label: 'Showroom', href: '#showroom' },
    { label: 'Contact', href: '#contact' },
];

const NavLink = ({ href, children }) => {
    const [hov, setHov] = useState(false);
    return (
        <a href={href}
            onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
            style={{
                position: 'relative', fontSize: '0.84rem', fontWeight: 500,
                color: hov ? C.text : C.sub, textDecoration: 'none',
                padding: '4px 0', transition: 'color .2s', fontFamily: F
            }}>
            {children}
            <span style={{
                position: 'absolute', bottom: 0, left: 0, height: '1px',
                borderRadius: 99, background: C.accent,
                width: hov ? '100%' : '0%', opacity: hov ? 1 : 0,
                transition: 'width .3s cubic-bezier(.19,1,.22,1), opacity .3s'
            }} />
        </a>
    );
};

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 36);
        window.addEventListener('scroll', fn, { passive: true });
        return () => window.removeEventListener('scroll', fn);
    }, []);

    return (
        <motion.nav
            initial={{ y: -72, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.0, ease: EXPO }}
            style={{
                position: 'fixed', top: 0, width: '100%', zIndex: 100,
                background: scrolled ? 'rgba(7,9,15,0.82)' : 'transparent',
                backdropFilter: scrolled ? 'blur(16px) saturate(140%)' : 'none',
                WebkitBackdropFilter: scrolled ? 'blur(16px) saturate(140%)' : 'none',
                borderBottom: scrolled ? `1px solid ${C.border}` : '1px solid transparent',
                transition: 'background .5s, border-color .5s',
            }}>
            <div style={{
                maxWidth: 1160, margin: '0 auto', padding: '0 1.5rem',
                height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>

                {/* Logo */}
                <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
                    <div style={{
                        width: 34, height: 34, borderRadius: 10, overflow: 'hidden',
                        border: `1px solid ${C.border}`, flexShrink: 0
                    }}>
                        <img src="/abha-logo.png" alt="Abha Enterprise"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    <div>
                        <p style={{
                            fontSize: '0.88rem', fontWeight: 600, color: C.text, margin: 0,
                            letterSpacing: '-0.02em', lineHeight: 1
                        }}>Abha Enterprise</p>
                        <p style={{
                            fontSize: '0.55rem', color: C.accent, letterSpacing: '0.2em',
                            textTransform: 'uppercase', margin: '3px 0 0', fontWeight: 600
                        }}>EV Mobility</p>
                    </div>
                </Link>

                {/* Desktop nav */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 32,
                    '@media(max-width:768px)': { display: 'none' }
                }}>
                    {NAV.map(l => <NavLink key={l.label} href={l.href}>{l.label}</NavLink>)}
                </div>

                {/* Desktop CTA — no Sign In, staff use /staff directly */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* Mobile hamburger */}
                    <button onClick={() => setOpen(p => !p)}
                        style={{ background: 'none', border: 'none', color: C.sub, cursor: 'pointer', padding: 4 }}>
                        {open ? <X style={{ width: 18, height: 18 }} /> : <Menu style={{ width: 18, height: 18 }} />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            <AnimatePresence>
                {open && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3, ease: EXPO }}
                        style={{
                            overflow: 'hidden', background: 'rgba(8,12,20,.97)',
                            backdropFilter: 'blur(20px)', borderTop: `1px solid ${C.border}`
                        }}>
                        <div style={{ padding: '16px 24px' }}>
                            {NAV.map((l, i) => (
                                <motion.a key={l.label} href={l.href} onClick={() => setOpen(false)}
                                    initial={{ x: -12, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: i * 0.05, duration: 0.35, ease: EXPO }}
                                    style={{
                                        display: 'block', padding: '13px 0', fontSize: '0.9rem',
                                        color: C.text, textDecoration: 'none',
                                        borderBottom: `1px solid ${C.border}`, fontFamily: F
                                    }}>
                                    {l.label}
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.nav>
    );
};

/* ─────────────────────────────────────────────
   HERO
───────────────────────────────────────────── */
const Hero = () => {
    const sRef = useRef(null);
    const h1Ref = useRef(null);
    const h2Ref = useRef(null);
    const subRef = useRef(null);
    const btnRef = useRef(null);
    const trustRef = useRef(null);
    const scooterRef = useRef(null);
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    const { scrollY } = useScroll();
    const yP = useTransform(scrollY, [0, 700], [0, -80]);
    const opP = useTransform(scrollY, [0, 500], [1, 0]);

    useLayoutEffect(() => {
        if (!h1Ref.current || !h2Ref.current) return;
        const s1 = new SplitType(h1Ref.current, { types: 'words,chars' });
        const s2 = new SplitType(h2Ref.current, { types: 'words,chars' });
        [s1, s2].forEach(s => (s.chars || []).forEach(c => {
            c.style.display = 'inline-block'; c.style.willChange = 'transform,opacity';
        }));
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ delay: 0.15 });
            tl.from(s1.chars, { yPercent: 110, opacity: 0, duration: 0.9, stagger: 0.025, ease: 'expo.out' }, 0)
                .from(s2.chars, { yPercent: 110, opacity: 0, duration: 0.9, stagger: 0.028, ease: 'expo.out' }, 0.18)
                .from(subRef.current, { y: 24, opacity: 0, duration: 0.8, ease: 'expo.out' }, 0.75)
                .from(btnRef.current?.children || [], { y: 16, opacity: 0, duration: 0.65, stagger: 0.1, ease: 'expo.out' }, 0.95)
                .from(trustRef.current?.children || [], { x: -10, opacity: 0, duration: 0.55, stagger: 0.07, ease: 'expo.out' }, 1.15)
                .from(scooterRef.current, { scale: 0.9, y: 24, opacity: 0, duration: 1.1, ease: 'expo.out' }, 0.4);
        }, sRef);
        return () => { ctx.revert(); s1.revert(); s2.revert(); };
    }, []);

    const onMove = useCallback(e => {
        mouseX.set(e.clientX - window.innerWidth / 2);
        mouseY.set(e.clientY - window.innerHeight / 2);
    }, [mouseX, mouseY]);

    return (
        <section ref={sRef} onMouseMove={onMove}
            style={{
                position: 'relative', minHeight: '100vh', display: 'flex',
                alignItems: 'center', overflow: 'hidden', background: C.bg
            }}>
            <AmbientBg />

            {/* top gradient fade */}
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 140,
                background: `linear-gradient(to bottom,${C.bg},transparent)`,
                pointerEvents: 'none', zIndex: 1
            }} />

            <div style={{
                maxWidth: 1200, margin: '0 auto', padding: 'clamp(100px,14vw,140px) 1.5rem 80px',
                width: '100%', position: 'relative', zIndex: 2
            }}>
                <motion.div style={{ y: yP, opacity: opP }}>
                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
                        gap: 'clamp(2rem,5vw,4rem)', alignItems: 'center'
                    }}>

                        {/* LEFT */}
                        <div>
                            {/* eyebrow */}
                            <motion.div initial={{ opacity: 0, y: 12, scale: 0.94 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.7, ease: EXPO }}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px',
                                    borderRadius: 999, marginBottom: 36, background: C.surface,
                                    border: `1px solid ${C.border}`
                                }}>
                                <motion.span animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.3, 1] }}
                                    transition={{ duration: 2.2, repeat: Infinity }}
                                    style={{
                                        width: 6, height: 6, borderRadius: '50%', background: C.green,
                                        display: 'inline-block', boxShadow: `0 0 8px ${C.green}`
                                    }} />
                                <span style={{
                                    fontSize: '0.68rem', color: C.sub, fontWeight: 600,
                                    letterSpacing: '0.14em', textTransform: 'uppercase'
                                }}>
                                    Premium EV Dealership · Jhargram
                                </span>
                            </motion.div>

                            {/* H1 */}
                            <h1 ref={h1Ref} style={{
                                fontFamily: F, fontWeight: 700,
                                fontSize: 'clamp(3.8rem,8.5vw,7.5rem)', lineHeight: 0.94,
                                letterSpacing: '-0.045em', color: C.text, margin: '0 0 2px', overflow: 'hidden'
                            }}>
                                Drive Electric.
                            </h1>
                            {/* H2 gradient */}
                            <h1 ref={h2Ref} style={{
                                fontFamily: F, fontWeight: 700,
                                fontSize: 'clamp(3.8rem,8.5vw,7.5rem)', lineHeight: 0.94,
                                letterSpacing: '-0.045em',
                                background: `linear-gradient(125deg,${C.accent} 0%,#a5b4fc 55%,#7dd3fc 100%)`,
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                margin: '0 0 40px', overflow: 'hidden', paddingBottom: '0.1em'
                            }}>
                                Move Smarter.
                            </h1>

                            <p ref={subRef} style={{
                                fontSize: 'clamp(1rem,1.6vw,1.08rem)', lineHeight: 1.75,
                                color: C.sub, maxWidth: 460, marginBottom: 40, fontFamily: F,
                                fontWeight: 400, letterSpacing: '-0.003em'
                            }}>
                                Abha Enterprise brings premium electric scooters, digital-first operations,
                                and a showroom experience engineered for the future of mobility.
                            </p>

                            <div ref={btnRef} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 48 }}>
                                <Btn href="#ev-collection" variant="primary" size="lg">
                                    <Zap style={{ width: 14, height: 14 }} /> Explore EVs
                                </Btn>
                                <Btn href="#contact" variant="ghost" size="lg">
                                    Book a Test Ride
                                </Btn>
                            </div>

                            <div ref={trustRef} style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                                {[
                                    { icon: Award, label: 'Authorised Dealer' },
                                    { icon: Shield, label: 'Warranty Assured' },
                                    { icon: Users, label: '500+ Customers' },
                                ].map(({ icon: Icon, label }) => (
                                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Icon style={{ width: 13, height: 13, color: C.muted }} />
                                        <span style={{ fontSize: '0.76rem', color: C.muted, fontWeight: 400, letterSpacing: '-0.003em' }}>{label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* RIGHT — scooter */}
                        <div ref={scooterRef} style={{
                            display: 'flex', alignItems: 'center',
                            justifyContent: 'center', minHeight: 500, position: 'relative'
                        }}>
                            <HeroScooter mouseX={mouseX} mouseY={mouseY} />
                        </div>
                    </div>
                </motion.div>

                {/* scroll cue */}
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2, duration: 1 }}
                    style={{
                        position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8
                    }}>
                    <span style={{
                        fontSize: '0.55rem', color: C.muted, letterSpacing: '0.28em',
                        textTransform: 'uppercase', fontWeight: 600
                    }}>Scroll</span>
                    <motion.div animate={{ y: [0, 7, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}>
                        <ChevronDown style={{ width: 14, height: 14, color: C.muted }} />
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

/* ─────────────────────────────────────────────
   WHY ELECTRIC
───────────────────────────────────────────── */
const BENEFITS = [
    { icon: TrendingUp, color: '#4F8CFF', bg: 'rgba(79,140,255,.07)', title: 'Lower Running Costs', stat: '₹0.50', unit: '/km', desc: 'Electricity costs a fraction of petrol — your wallet feels the difference every single day.' },
    { icon: Leaf, color: '#22C55E', bg: 'rgba(34,197,94,.07)', title: 'Zero Emissions', stat: '0', unit: 'g CO₂', desc: 'Every kilometre you ride electric is a step toward cleaner air. No exhaust. No guilt.' },
    { icon: Volume2, color: '#a5b4fc', bg: 'rgba(165,180,252,.07)', title: 'Silent Mobility', stat: '<40', unit: 'dB', desc: 'Glide through the city in near-total silence. No engine noise. No vibration.' },
    { icon: Cpu, color: '#7dd3fc', bg: 'rgba(125,211,252,.07)', title: 'Smart Technology', stat: 'IoT', unit: 'Ready', desc: 'Connected dashboards, app-based diagnostics, and digital service tracking built in.' },
];

const WhyElectric = () => {
    const sRef = useRef(null);
    useLayoutEffect(() => {
        if (!sRef.current) return;
        const ctx = gsap.context(() => {
            gsap.from(sRef.current.querySelectorAll('[data-card]'), {
                y: 40, opacity: 0, duration: 0.9, stagger: 0.1, ease: 'expo.out',
                scrollTrigger: { trigger: sRef.current, start: 'top 74%', toggleActions: 'play none none none' },
            });
            sRef.current.querySelectorAll('[data-count]').forEach(el => {
                const v = el.dataset.count;
                if (isNaN(parseFloat(v))) return;
                ScrollTrigger.create({
                    trigger: el, start: 'top 88%', once: true,
                    onEnter: () => anime({
                        targets: el, innerHTML: [0, parseFloat(v)],
                        easing: 'easeOutExpo', duration: 1500, round: 1,
                        update: a => { el.innerHTML = parseFloat(a.animations[0].currentValue).toLocaleString(); }
                    })
                });
            });
        }, sRef);
        return () => ctx.revert();
    }, []);

    return (
        <section id="why-electric" ref={sRef}
            style={{
                padding: 'clamp(5rem,10vw,9rem) 1.5rem', position: 'relative',
                overflow: 'hidden', background: C.bg
            }}>
            {/* section divider line */}
            <div style={{
                position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                width: '1px', height: 80, background: `linear-gradient(to bottom,transparent,${C.border})`
            }} />

            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: 'clamp(3rem,6vw,5rem)' }}>
                    <Reveal y={10}>
                        <p style={{
                            fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.3em',
                            textTransform: 'uppercase', color: C.accent, marginBottom: 20
                        }}>The Future Is Electric</p>
                    </Reveal>
                    <SplitReveal as="h2" stagger={0.07}
                        style={{
                            fontFamily: F, fontSize: 'clamp(2rem,4.5vw,3.8rem)', fontWeight: 600,
                            letterSpacing: '-0.035em', color: C.text, margin: '0 0 20px', lineHeight: 1.08
                        }}>
                        Cleaner. Smarter. Future-Ready.
                    </SplitReveal>
                    <Reveal delay={0.25} y={14}>
                        <p style={{
                            fontSize: '1.05rem', color: C.sub, maxWidth: 540, margin: '0 auto',
                            lineHeight: 1.7
                        }}>
                            Electric mobility isn't just about the environment — it's a smarter, more efficient way to move.
                        </p>
                    </Reveal>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: '1px', border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden' }}>
                    {BENEFITS.map((b, idx) => {
                        const Icon = b.icon;
                        return (
                            <div key={b.title} data-card>
                                <motion.div whileHover={{ background: C.surfaceAlt }}
                                    transition={{ duration: 0.25 }}
                                    style={{
                                        padding: '2rem 1.75rem', background: C.surface,
                                        height: '100%', borderRight: idx < 3 ? `1px solid ${C.border}` : 'none',
                                    }}>
                                    <div style={{
                                        width: 40, height: 40, borderRadius: 10, marginBottom: 20,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        background: b.bg,
                                    }}>
                                        <Icon style={{ width: 18, height: 18, color: b.color }} />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginBottom: 2 }}>
                                        <span data-count={b.stat}
                                            style={{ fontSize: '1.75rem', fontWeight: 700, color: b.color, letterSpacing: '-0.04em', lineHeight: 1 }}>{b.stat}</span>
                                        <span style={{ fontSize: '0.82rem', fontWeight: 500, color: b.color, opacity: 0.8 }}>{b.unit}</span>
                                    </div>
                                    <h3 style={{ fontSize: '0.88rem', fontWeight: 600, color: C.text, margin: '12px 0 6px', letterSpacing: '-0.01em' }}>{b.title}</h3>
                                    <p style={{ fontSize: '0.82rem', lineHeight: 1.65, color: C.sub, margin: 0 }}>{b.desc}</p>
                                </motion.div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

/* ─────────────────────────────────────────────
   ABOUT
───────────────────────────────────────────── */
const About = () => {
    const sRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: sRef, offset: ['start end', 'end start'] });
    const cardY = useSpring(useTransform(scrollYProgress, [0, 1], [24, -24]), { stiffness: 45, damping: 22 });
    const copyY = useSpring(useTransform(scrollYProgress, [0, 1], [10, -10]), { stiffness: 55, damping: 22 });

    return (
        <section id="about" ref={sRef}
            style={{
                padding: 'clamp(5rem,10vw,9rem) 1.5rem', background: C.bgAlt,
                overflow: 'hidden', position: 'relative'
            }}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
                    gap: 'clamp(2.5rem,6vw,5rem)', alignItems: 'center'
                }}>

                    {/* Card */}
                    <motion.div style={{ y: cardY, position: 'relative' }}>
                        <motion.div whileHover={{ borderColor: C.borderHov }}
                            transition={{ duration: 0.3 }}
                            style={{
                                borderRadius: 24, padding: '2.5rem', position: 'relative', overflow: 'hidden',
                                background: C.surface, border: `1px solid ${C.border}`,
                                boxShadow: '0 24px 60px rgba(0,0,0,0.3)'
                            }}>
                            {/* top accent line */}
                            <div style={{
                                position: 'absolute', inset: '0 0 auto', height: 1,
                                background: `linear-gradient(90deg,transparent,${C.accent},transparent)`
                            }} />

                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
                                <div style={{
                                    width: 48, height: 48, borderRadius: 14, overflow: 'hidden',
                                    border: `1px solid ${C.border}`, flexShrink: 0
                                }}>
                                    <img src="/abha-logo.png" alt="Abha Enterprise"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                                <div>
                                    <p style={{
                                        fontSize: '1.05rem', fontWeight: 600, color: C.text, margin: 0,
                                        letterSpacing: '-0.02em'
                                    }}>Abha Enterprise</p>
                                    <p style={{ fontSize: '0.75rem', color: C.accent, margin: '3px 0 0', fontWeight: 500 }}>
                                        Authorised EV Dealership
                                    </p>
                                </div>
                            </div>

                            <Stagger stagger={0.07}
                                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
                                {[
                                    { value: '500+', label: 'EVs Delivered', color: C.accent },
                                    { value: '5+', label: 'Years', color: C.green },
                                    { value: '15+', label: 'EV Brands', color: '#818cf8' },
                                    { value: '24/7', label: 'Service Support', color: '#38bdf8' },
                                ].map(s => (
                                    <SI key={s.label} y={12}>
                                        <motion.div whileHover={{ borderColor: `${s.color}40` }} transition={{ duration: 0.25 }}
                                            style={{
                                                padding: '1rem', borderRadius: 14, background: C.bgAlt,
                                                border: `1px solid ${C.border}`
                                            }}>
                                            <p style={{
                                                fontSize: '1.5rem', fontWeight: 700, color: s.color, margin: 0,
                                                letterSpacing: '-0.03em', lineHeight: 1
                                            }}>{s.value}</p>
                                            <p style={{
                                                fontSize: '0.7rem', color: C.muted, margin: '6px 0 0',
                                                fontWeight: 500
                                            }}>{s.label}</p>
                                        </motion.div>
                                    </SI>
                                ))}
                            </Stagger>

                            <p style={{
                                fontSize: '0.62rem', color: C.muted, textTransform: 'uppercase',
                                letterSpacing: '0.16em', marginBottom: 12, fontWeight: 600
                            }}>Authorised For</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {['Ola Electric', 'TVS', 'Zelio', 'Ampere', 'Ather'].map((brand, i) => (
                                    <motion.span key={brand}
                                        initial={{ opacity: 0, scale: 0.88 }} whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: i * 0.07, duration: 0.45, ease: EXPO }} viewport={{ once: true }}
                                        whileHover={{ borderColor: C.accent, color: C.accent }}
                                        style={{
                                            padding: '6px 14px', borderRadius: 999, fontSize: '0.75rem',
                                            fontWeight: 500, color: C.sub, background: C.bgAlt,
                                            border: `1px solid ${C.border}`, cursor: 'default',
                                            transition: 'border-color .25s, color .25s'
                                        }}>
                                        {brand}
                                    </motion.span>
                                ))}
                            </div>
                        </motion.div>

                        {/* floating badge */}
                        <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
                            style={{
                                position: 'absolute', bottom: -22, right: -16,
                                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px',
                                borderRadius: 16, background: C.surface, border: `1px solid ${C.border}`,
                                boxShadow: '0 16px 40px rgba(0,0,0,0.35)'
                            }}>
                            <div style={{
                                width: 30, height: 30, borderRadius: 9,
                                background: 'rgba(34,197,94,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <CheckCircle2 style={{ width: 15, height: 15, color: C.green }} />
                            </div>
                            <div>
                                <p style={{
                                    fontSize: '0.58rem', color: C.muted, margin: 0,
                                    textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600
                                }}>Certified</p>
                                <p style={{
                                    fontSize: '0.88rem', fontWeight: 600, color: C.text,
                                    margin: '2px 0 0'
                                }}>ISO Compliant</p>
                            </div>
                        </motion.div>
                    </motion.div>

                    {/* Copy */}
                    <motion.div style={{ y: copyY, position: 'relative' }}>
                        {/* Subtle decorative vector — right side background */}
                        <svg
                            aria-hidden="true"
                            viewBox="0 0 320 480"
                            width="320" height="480"
                            fill="none"
                            style={{
                                position: 'absolute', right: -48, top: '50%',
                                transform: 'translateY(-50%)',
                                opacity: 0.045, pointerEvents: 'none', zIndex: 0,
                                overflow: 'visible'
                            }}>
                            {/* Concentric arcs */}
                            <path d="M 280 240 A 80 80 0 0 1 200 320" stroke={C.accent} strokeWidth="1" strokeLinecap="round" />
                            <path d="M 300 240 A 100 100 0 0 1 200 340" stroke={C.accent} strokeWidth="0.8" strokeLinecap="round" />
                            <path d="M 320 240 A 120 120 0 0 1 200 360" stroke={C.accent} strokeWidth="0.6" strokeLinecap="round" />
                            <path d="M 260 240 A 60 60 0 0 1 200 300" stroke={C.accent} strokeWidth="1.2" strokeLinecap="round" />
                            {/* Vertical dotted line */}
                            <line x1="200" y1="60" x2="200" y2="420" stroke={C.accent} strokeWidth="0.8" strokeDasharray="4 8" />
                            {/* Horizontal dotted line */}
                            <line x1="60" y1="240" x2="310" y2="240" stroke={C.accent} strokeWidth="0.8" strokeDasharray="4 8" />
                            {/* Small cross marks */}
                            {[[200, 120], [200, 360], [120, 240], [280, 240]].map(([cx, cy], i) => (
                                <g key={i}>
                                    <line x1={cx - 5} y1={cy} x2={cx + 5} y2={cy} stroke={C.accent} strokeWidth="1" />
                                    <line x1={cx} y1={cy - 5} x2={cx} y2={cy + 5} stroke={C.accent} strokeWidth="1" />
                                </g>
                            ))}
                            {/* Corner bracket top-right */}
                            <path d="M 270 80 L 300 80 L 300 110" stroke={C.accent} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            {/* Corner bracket bottom-left */}
                            <path d="M 130 370 L 100 370 L 100 400" stroke={C.accent} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            {/* Small dots scattered */}
                            {[[240, 160], [160, 300], [290, 310], [110, 170]].map(([cx, cy], i) => (
                                <circle key={i} cx={cx} cy={cy} r="2" fill={C.accent} />
                            ))}
                            {/* Diagonal slash lines */}
                            <line x1="240" y1="80" x2="260" y2="100" stroke={C.accent} strokeWidth="0.8" />
                            <line x1="140" y1="380" x2="160" y2="400" stroke={C.accent} strokeWidth="0.8" />
                        </svg>
                        <Reveal y={12}>
                            <p style={{
                                fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.3em',
                                textTransform: 'uppercase', color: C.accent, marginBottom: 18
                            }}>About Us</p>
                        </Reveal>
                        <SplitReveal as="h2" stagger={0.065}
                            style={{
                                fontFamily: F, fontSize: 'clamp(1.8rem,3.8vw,3.2rem)', fontWeight: 600,
                                letterSpacing: '-0.035em', color: C.text, margin: '0 0 20px', lineHeight: 1.1
                            }}>
                            Jhargram's Premier Electric Mobility Partner.
                        </SplitReveal>
                        <Reveal delay={0.18} y={14}>
                            <p style={{
                                fontSize: '1rem', lineHeight: 1.75, color: C.sub, marginBottom: 32,
                                maxWidth: 520
                            }}>
                                Abha Enterprise is a technology-driven EV dealership committed to making electric
                                mobility accessible, reliable, and premium. We combine authorised dealership
                                expertise with digital-first operations to deliver an unmatched customer experience.
                            </p>
                        </Reveal>
                        <Stagger stagger={0.09} delay={0.2}>
                            {[
                                { title: 'Authorised Multi-Brand Dealer', desc: 'Official dealership for Ola Electric, TVS, Zelio, Ampere, Ather and more.' },
                                { title: 'Digital Operations', desc: 'Smart inventory, Android task management, real-time service tracking.' },
                                { title: 'End-to-End Support', desc: "From test ride to delivery to after-sales — we're with you every step." },
                                { title: 'GST Compliant Billing', desc: 'Transparent, compliant invoicing with full documentation support.' },
                            ].map(item => (
                                <SI key={item.title} y={14}>
                                    <motion.div whileHover={{ x: 5, background: C.surfaceAlt }} transition={{ duration: 0.22 }}
                                        style={{
                                            display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 16px',
                                            borderRadius: 14, marginBottom: 6, border: `1px solid ${C.border}`
                                        }}>
                                        <div style={{
                                            width: 20, height: 20, borderRadius: '50%', background: C.accentDim,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0, marginTop: 2
                                        }}>
                                            <CheckCircle2 style={{ width: 12, height: 12, color: C.accent }} />
                                        </div>
                                        <div>
                                            <p style={{
                                                fontSize: '0.9rem', fontWeight: 600, color: C.text, margin: 0,
                                                letterSpacing: '-0.01em'
                                            }}>{item.title}</p>
                                            <p style={{
                                                fontSize: '0.82rem', color: C.sub, margin: '3px 0 0',
                                                lineHeight: 1.55
                                            }}>{item.desc}</p>
                                        </div>
                                    </motion.div>
                                </SI>
                            ))}
                        </Stagger>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

/* ─────────────────────────────────────────────
   EV COLLECTION
───────────────────────────────────────────── */
const EV_MODELS = [
    {
        brand: 'Ola Electric', name: 'S1 Pro', range: '195', speed: '120', charge: '4',
        color: '#4F8CFF', badge: 'Best Seller',
        image: '/OlaS1Pro.png',
        desc: "195 km IDC range, 120 km/h top speed, 7\" touchscreen with OTA updates — India's most advanced electric scooter."
    },
    {
        brand: 'TVS', name: 'iQube', range: '145', speed: '78', charge: '4.7',
        color: '#818cf8', badge: 'Premium',
        image: '/TVS-iQube.png',
        desc: 'IntelliGo technology, hill hold, turn-by-turn navigation, and 145 km IDC range on the 3.5 kWh variant.'
    },
    {
        brand: 'Zelio', name: 'Eeva', range: '120', speed: '25', charge: '4',
        color: '#22C55E', badge: 'Popular',
        image: '/Zelio.png',
        desc: 'License-free urban commuter with up to 120 km range, 36-litre boot, and ultra-low running cost of ₹0.3/km.'
    },
    {
        brand: 'Ampere', name: 'Primus', range: '107', speed: '77', charge: '5',
        color: '#38bdf8', badge: 'New Arrival',
        image: '/AmperePrimus.png',
        desc: '3 kWh LFP battery, 77 km/h top speed, fast-charge ready — Greaves Electric\'s most capable scooter.'
    },
];

const EVCard = ({ model, idx }) => {
    const c = model.color;
    const [hov, setHov] = useState(false);
    return (
        <SI y={44}>
            <motion.div
                onHoverStart={() => setHov(true)} onHoverEnd={() => setHov(false)}
                whileHover={{ y: -8, borderColor: `${c}35` }}
                transition={{ duration: 0.38, ease: EXPO }}
                style={{
                    borderRadius: 20, overflow: 'hidden', background: C.surface,
                    border: `1px solid ${C.border}`, position: 'relative',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.25)', height: '100%'
                }}>

                {/* top accent on hover */}
                <motion.div animate={{ opacity: hov ? 1 : 0 }} transition={{ duration: 0.3 }}
                    style={{
                        position: 'absolute', inset: '0 0 auto', height: 1,
                        background: `linear-gradient(90deg,transparent,${c},transparent)`
                    }} />

                {/* visual area */}
                <div style={{
                    height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative', background: `linear-gradient(135deg,${c}08 0%,${C.bgAlt} 100%)`,
                    overflow: 'hidden'
                }}>
                    {/* subtle ambient glow */}
                    <motion.div animate={{ opacity: hov ? 0.35 : 0.1, scale: hov ? 1.08 : 1 }}
                        transition={{ duration: 0.5 }}
                        style={{
                            position: 'absolute', width: 200, height: 200, borderRadius: '50%',
                            background: `radial-gradient(circle,${c}55,transparent 70%)`, filter: 'blur(32px)'
                        }} />

                    <motion.div animate={{ scale: hov ? 1.05 : 1, y: hov ? -6 : 0 }}
                        transition={{ duration: 0.45, ease: EXPO }}
                        style={{ position: 'relative', zIndex: 1, width: '85%', height: '85%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img
                            src={model.image}
                            alt={model.name}
                            style={{
                                width: '100%', height: '100%', objectFit: 'contain',
                                filter: `drop-shadow(0 8px 24px ${c}40)`,
                                mixBlendMode: 'screen',
                                transition: 'filter .4s',
                                transform: model.name === 'iQube' ? 'scale(0.9)' : undefined,
                            }}
                        />
                    </motion.div>

                    <div style={{
                        position: 'absolute', top: 14, left: 14, padding: '5px 12px',
                        borderRadius: 999, fontSize: '0.65rem', fontWeight: 600, color: c,
                        background: C.surface, border: `1px solid ${c}35`, letterSpacing: '0.04em'
                    }}>
                        {model.badge}
                    </div>
                    <div style={{
                        position: 'absolute', top: 14, right: 14, padding: '4px 10px',
                        borderRadius: 7, fontSize: '0.6rem', fontWeight: 500, color: C.muted,
                        background: 'rgba(13,18,32,.75)', backdropFilter: 'blur(8px)'
                    }}>
                        Electric Scooter
                    </div>
                </div>

                {/* content */}
                <div style={{ padding: '1.5rem' }}>
                    <p style={{
                        fontSize: '0.65rem', color: C.muted, textTransform: 'uppercase',
                        letterSpacing: '0.14em', margin: '0 0 5px', fontWeight: 600
                    }}>{model.brand}</p>
                    <h3 style={{
                        fontSize: '1.3rem', fontWeight: 700, color: C.text, margin: '0 0 10px',
                        letterSpacing: '-0.025em'
                    }}>{model.name}</h3>
                    <p style={{
                        fontSize: '0.84rem', lineHeight: 1.6, color: C.sub,
                        margin: '0 0 18px'
                    }}>{model.desc}</p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 18 }}>
                        {[
                            { icon: Battery, label: 'Range', value: model.range, unit: 'km' },
                            { icon: Gauge, label: 'Speed', value: model.speed, unit: 'km/h' },
                            { icon: Zap, label: 'Charge', value: model.charge, unit: 'hrs' },
                        ].map(({ icon: Icon, label, value, unit }) => (
                            <motion.div key={label} whileHover={{ borderColor: `${c}35` }}
                                transition={{ duration: 0.2 }}
                                style={{
                                    textAlign: 'center', padding: '10px 6px', borderRadius: 12,
                                    background: C.bgAlt, border: `1px solid ${C.border}`
                                }}>
                                <Icon style={{ width: 13, height: 13, color: c, display: 'block', margin: '0 auto 5px' }} />
                                <p style={{ fontSize: '0.84rem', fontWeight: 700, color: C.text, margin: 0 }}>
                                    {value}<span style={{ fontSize: '0.6rem', color: C.muted, marginLeft: 2 }}>{unit}</span>
                                </p>
                                <p style={{ fontSize: '0.58rem', color: C.muted, margin: '2px 0 0', fontWeight: 500 }}>{label}</p>
                            </motion.div>
                        ))}
                    </div>

                    <Btn href="#contact" variant="subtle" size="sm" fullWidth>
                        Book Test Ride <ArrowUpRight style={{ width: 13, height: 13 }} />
                    </Btn>
                </div>
            </motion.div>
        </SI>
    );
};

const EVCollection = () => (
    <section id="ev-collection"
        style={{
            padding: 'clamp(5rem,10vw,9rem) 1.5rem', position: 'relative',
            overflow: 'hidden', background: C.bgAlt
        }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: 'clamp(3rem,6vw,5rem)' }}>
                <Reveal y={10}>
                    <p style={{
                        fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.3em',
                        textTransform: 'uppercase', color: C.accent, marginBottom: 20
                    }}>Our EV Lineup</p>
                </Reveal>
                <SplitReveal as="h2" stagger={0.07}
                    style={{
                        fontFamily: F, fontSize: 'clamp(2rem,4.5vw,3.8rem)', fontWeight: 600,
                        letterSpacing: '-0.035em', color: C.text, margin: '0 0 20px', lineHeight: 1.08
                    }}>
                    Choose Your Electric Ride.
                </SplitReveal>
                <Reveal delay={0.25} y={14}>
                    <p style={{ fontSize: '1.05rem', color: C.sub, maxWidth: 520, margin: '0 auto', lineHeight: 1.7 }}>
                        Premium electric scooters from India's most trusted brands — all under one roof.
                    </p>
                </Reveal>
            </div>

            <Stagger stagger={0.1}
                style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: '1rem' }}>
                {EV_MODELS.map((m, i) => <EVCard key={m.name} model={m} idx={i} />)}
            </Stagger>

            <Reveal delay={0.2} style={{ textAlign: 'center', marginTop: '3.5rem' }}>
                <Btn href="#contact" variant="ghost" size="md">
                    View Full Catalogue <ArrowRight style={{ width: 14, height: 14 }} />
                </Btn>
            </Reveal>
        </div>
    </section>
);

/* ─────────────────────────────────────────────
   SMART OPERATIONS
───────────────────────────────────────────── */
const OPS = [
    { icon: Package, color: '#4F8CFF', title: 'Smart Inventory', desc: 'Real-time EV stock across models, variants, and accessories.' },
    { icon: Smartphone, color: '#818cf8', title: 'Android Task Management', desc: 'Field teams use Android apps for service, prep, and delivery coordination.' },
    { icon: BarChart3, color: '#22C55E', title: 'Digital Workflows', desc: 'From EV arrival to customer handover — every step tracked and optimised.' },
    { icon: Wrench, color: '#38bdf8', title: 'Service Coordination', desc: 'Scheduled maintenance, warranty tracking, and service history unified.' },
];

const SmartOperations = () => {
    const sRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: sRef, offset: ['start end', 'end start'] });
    const phoneY = useSpring(useTransform(scrollYProgress, [0, 1], [50, -50]), { stiffness: 45, damping: 20 });

    return (
        <section id="operations" ref={sRef}
            style={{
                padding: 'clamp(5rem,10vw,9rem) 1.5rem', position: 'relative',
                overflow: 'hidden', background: C.bg
            }}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
                    gap: 'clamp(2.5rem,6vw,5rem)', alignItems: 'center'
                }}>

                    {/* Left */}
                    <div>
                        <Reveal y={10}>
                            <p style={{
                                fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.3em',
                                textTransform: 'uppercase', color: C.accent, marginBottom: 18
                            }}>Operational Excellence</p>
                        </Reveal>
                        <SplitReveal as="h2" stagger={0.065}
                            style={{
                                fontFamily: F, fontSize: 'clamp(1.8rem,3.8vw,3.2rem)', fontWeight: 600,
                                letterSpacing: '-0.035em', color: C.text, margin: '0 0 20px', lineHeight: 1.1
                            }}>
                            Powered by Smart Digital Systems.
                        </SplitReveal>
                        <Reveal delay={0.18} y={14}>
                            <p style={{ fontSize: '1rem', lineHeight: 1.75, color: C.sub, marginBottom: 32, maxWidth: 500 }}>
                                Behind every smooth customer experience is a well-oiled digital operation.
                                Abha Enterprise uses technology to coordinate inventory, service, and delivery with precision.
                            </p>
                        </Reveal>
                        <Stagger stagger={0.09} delay={0.2}>
                            {OPS.map(f => {
                                const Icon = f.icon;
                                return (
                                    <SI key={f.title} y={16}>
                                        <motion.div whileHover={{ x: 5, background: C.surfaceAlt, borderColor: C.borderHov }}
                                            transition={{ duration: 0.22 }}
                                            style={{
                                                display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 18px',
                                                borderRadius: 16, marginBottom: 8, border: `1px solid ${C.border}`
                                            }}>
                                            <div style={{
                                                width: 38, height: 38, borderRadius: 11, flexShrink: 0,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                background: `${f.color}12`, border: `1px solid ${f.color}22`
                                            }}>
                                                <Icon style={{ width: 17, height: 17, color: f.color }} />
                                            </div>
                                            <div>
                                                <p style={{
                                                    fontSize: '0.9rem', fontWeight: 600, color: C.text, margin: 0,
                                                    letterSpacing: '-0.01em'
                                                }}>{f.title}</p>
                                                <p style={{
                                                    fontSize: '0.82rem', color: C.sub, margin: '4px 0 0',
                                                    lineHeight: 1.55
                                                }}>{f.desc}</p>
                                            </div>
                                        </motion.div>
                                    </SI>
                                );
                            })}
                        </Stagger>
                    </div>

                    {/* Phone mockup */}
                    <Reveal delay={0.12} y={36}>
                        <motion.div style={{ y: phoneY, display: 'flex', justifyContent: 'center', position: 'relative' }}>
                            <div style={{
                                position: 'absolute', width: 260, height: 260, borderRadius: '50%',
                                background: `radial-gradient(circle,${C.accentDim},transparent 70%)`,
                                filter: 'blur(40px)', pointerEvents: 'none', opacity: 0.6
                            }} />

                            <motion.div whileHover={{ scale: 1.02, y: -5 }} transition={{ duration: 0.35, ease: EXPO }}
                                style={{
                                    width: 260, borderRadius: 40, overflow: 'hidden', position: 'relative', zIndex: 1,
                                    background: '#070b12', border: `2px solid rgba(15,23,42,.95)`,
                                    boxShadow: '0 40px 80px rgba(0,0,0,.5), 0 0 0 1px rgba(79,140,255,.08)'
                                }}>
                                <div style={{ borderRadius: 34, overflow: 'hidden', background: '#0a0f1c', margin: 3 }}>
                                    {/* status bar */}
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '12px 20px 6px'
                                    }}>
                                        <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,.45)', fontWeight: 500 }}>9:41</span>
                                        <div style={{ width: 70, height: 18, borderRadius: 999, background: '#000' }} />
                                        <div style={{ width: 12, height: 8, borderRadius: 2, background: 'rgba(74,222,128,.65)' }} />
                                    </div>
                                    {/* app header */}
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px',
                                        borderBottom: '1px solid rgba(255,255,255,.05)'
                                    }}>
                                        <div style={{ width: 26, height: 26, borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                                            <img src="/abha-logo.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <p style={{ fontSize: '0.72rem', fontWeight: 600, color: '#fff', margin: 0 }}>Abha Operations</p>
                                            <p style={{
                                                fontSize: '0.55rem', color: '#4ade80', margin: '1px 0 0', fontWeight: 500,
                                                animation: 'blink 2s ease-in-out infinite'
                                            }}>● Live</p>
                                        </div>
                                    </div>
                                    {/* content */}
                                    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {/* inventory */}
                                        <div style={{
                                            padding: 12, borderRadius: 14,
                                            background: 'rgba(79,140,255,.1)', border: '1px solid rgba(79,140,255,.18)'
                                        }}>
                                            <p style={{
                                                fontSize: '0.54rem', color: 'rgba(147,197,253,.8)',
                                                textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 8px', fontWeight: 600
                                            }}>
                                                Inventory Status
                                            </p>
                                            {[
                                                { model: 'Ola S1 Pro', pct: 53, color: C.accent },
                                                { model: 'TVS iQube', pct: 33, color: '#818cf8' },
                                                { model: 'Ampere Primus', pct: 80, color: '#34d399' },
                                            ].map(item => (
                                                <div key={item.model} style={{
                                                    display: 'flex', alignItems: 'center',
                                                    justifyContent: 'space-between', marginBottom: 6
                                                }}>
                                                    <span style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,.6)' }}>{item.model}</span>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                        <div style={{
                                                            width: 56, height: 4, borderRadius: 999,
                                                            background: 'rgba(255,255,255,.08)', overflow: 'hidden'
                                                        }}>
                                                            <motion.div initial={{ width: 0 }} whileInView={{ width: `${item.pct}%` }}
                                                                viewport={{ once: true }} transition={{ duration: 1.1, delay: 0.3, ease: EXPO }}
                                                                style={{ height: '100%', borderRadius: 999, background: item.color }} />
                                                        </div>
                                                        <span style={{ fontSize: '0.58rem', fontWeight: 600, color: item.color }}>
                                                            {Math.round(item.pct / 6.67)}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        {/* tasks */}
                                        <p style={{
                                            fontSize: '0.54rem', color: 'rgba(255,255,255,.3)',
                                            textTransform: 'uppercase', letterSpacing: '0.12em', margin: 0, fontWeight: 600
                                        }}>
                                            Today's Tasks
                                        </p>
                                        {[
                                            { task: 'EV Delivery — Rajesh K.', color: '#34d399', status: 'done' },
                                            { task: 'Service — iQube #TVS-042', color: C.accent, status: 'active' },
                                            { task: 'Barcode Scan — New Stock', color: '#38bdf8', status: 'pending' },
                                            { task: 'Invoice — Priya M.', color: '#818cf8', status: 'pending' },
                                        ].map((t, i) => (
                                            <motion.div key={i} initial={{ opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }}
                                                viewport={{ once: true }} transition={{ delay: 0.35 + i * 0.07, duration: 0.35, ease: EXPO }}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                                                    borderRadius: 10, background: 'rgba(255,255,255,.03)',
                                                    border: '1px solid rgba(255,255,255,.04)'
                                                }}>
                                                <div style={{
                                                    width: 6, height: 6, borderRadius: '50%',
                                                    background: t.color, flexShrink: 0
                                                }} />
                                                <span style={{
                                                    fontSize: '0.62rem', color: 'rgba(255,255,255,.6)', flex: 1,
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                                }}>{t.task}</span>
                                                <span style={{
                                                    fontSize: '0.54rem', padding: '2px 7px', borderRadius: 999,
                                                    background: `${t.color}20`, color: t.color, fontWeight: 600
                                                }}>{t.status}</span>
                                            </motion.div>
                                        ))}
                                        <button style={{
                                            width: '100%', padding: '10px', borderRadius: 12,
                                            fontSize: '0.7rem', fontWeight: 600, color: '#93c5fd',
                                            background: 'rgba(79,140,255,.12)', border: '1px solid rgba(79,140,255,.25)',
                                            cursor: 'pointer', display: 'flex', alignItems: 'center',
                                            justifyContent: 'center', gap: 6
                                        }}>
                                            <Package style={{ width: 12, height: 12 }} /> Scan Barcode
                                        </button>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 12px' }}>
                                        <div style={{
                                            width: 80, height: 3, borderRadius: 999,
                                            background: 'rgba(255,255,255,.18)'
                                        }} />
                                    </div>
                                </div>
                            </motion.div>

                            {/* side cards */}
                            <motion.div animate={{ y: [0, -9, 0] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                                style={{
                                    position: 'absolute', top: 20, right: -16, display: 'flex', alignItems: 'center',
                                    gap: 9, padding: '10px 14px', borderRadius: 14, background: C.surface,
                                    border: `1px solid ${C.border}`, boxShadow: '0 10px 30px rgba(0,0,0,.3)'
                                }}>
                                <CheckCircle2 style={{ width: 14, height: 14, color: C.green }} />
                                <div>
                                    <p style={{
                                        fontSize: '0.54rem', color: C.muted, margin: 0,
                                        textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600
                                    }}>Delivered</p>
                                    <p style={{ fontSize: '0.84rem', fontWeight: 700, color: C.text, margin: '1px 0 0' }}>3 EVs</p>
                                </div>
                            </motion.div>
                            <motion.div animate={{ y: [0, -11, 0] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                                style={{
                                    position: 'absolute', bottom: 60, left: -16, display: 'flex', alignItems: 'center',
                                    gap: 9, padding: '10px 14px', borderRadius: 14, background: C.surface,
                                    border: `1px solid ${C.border}`, boxShadow: '0 10px 30px rgba(0,0,0,.3)'
                                }}>
                                <Clock style={{ width: 14, height: 14, color: '#38bdf8' }} />
                                <div>
                                    <p style={{
                                        fontSize: '0.54rem', color: C.muted, margin: 0,
                                        textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600
                                    }}>Avg. Service</p>
                                    <p style={{ fontSize: '0.84rem', fontWeight: 700, color: C.text, margin: '1px 0 0' }}>2.4 hrs</p>
                                </div>
                            </motion.div>
                        </motion.div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

/* ─────────────────────────────────────────────
   SHOWROOM
───────────────────────────────────────────── */
const SHOWROOM_FEATS = [
    { icon: Star, title: 'Premium Showroom', desc: 'A modern, well-lit space designed to showcase every EV at its best.' },
    { icon: Zap, title: 'Live Charging Demo', desc: 'See real-time charging. Understand range and battery performance firsthand.' },
    { icon: Wind, title: 'Test Rides', desc: 'Take any model for a spin. Feel the silent acceleration yourself.' },
    { icon: Shield, title: 'Expert Guidance', desc: 'Our trained team helps you choose the right EV for your lifestyle.' },
];

const Showroom = () => (
    <section id="showroom"
        style={{
            padding: 'clamp(5rem,10vw,9rem) 1.5rem', position: 'relative',
            overflow: 'hidden', background: C.bgAlt
        }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: 'clamp(3rem,6vw,4.5rem)' }}>
                <Reveal y={10}>
                    <p style={{
                        fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.3em',
                        textTransform: 'uppercase', color: C.accent, marginBottom: 20
                    }}>Visit Us</p>
                </Reveal>
                <SplitReveal as="h2" stagger={0.07}
                    style={{
                        fontFamily: F, fontSize: 'clamp(2rem,4.5vw,3.8rem)', fontWeight: 600,
                        letterSpacing: '-0.035em', color: C.text, margin: '0 0 20px', lineHeight: 1.08
                    }}>
                    The Showroom Experience.
                </SplitReveal>
                <Reveal delay={0.25} y={14}>
                    <p style={{ fontSize: '1.05rem', color: C.sub, maxWidth: 520, margin: '0 auto', lineHeight: 1.7 }}>
                        More than a dealership — a destination for anyone curious about the future of mobility.
                    </p>
                </Reveal>
            </div>

            <Reveal y={28}>
                <motion.div whileHover={{ borderColor: C.borderHov }} transition={{ duration: 0.3 }}
                    style={{
                        borderRadius: 24, overflow: 'hidden', position: 'relative',
                        background: C.surface, border: `1px solid ${C.border}`,
                        boxShadow: '0 20px 50px rgba(0,0,0,.3)'
                    }}>
                    {/* top line */}
                    <div style={{
                        position: 'absolute', inset: '0 0 auto', height: 1,
                        background: `linear-gradient(90deg,transparent,${C.accent},transparent)`
                    }} />

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))' }}>
                        {/* main copy */}
                        <div style={{ padding: 'clamp(2rem,4vw,3.5rem)', position: 'relative', gridColumn: 'span 2', minWidth: 0 }}>
                            <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px',
                                borderRadius: 999, marginBottom: 24, background: C.bgAlt, border: `1px solid ${C.border}`
                            }}>
                                <motion.span animate={{ opacity: [0.3, 1, 0.3], scale: [1, 1.3, 1] }}
                                    transition={{ duration: 2.2, repeat: Infinity }}
                                    style={{
                                        width: 6, height: 6, borderRadius: '50%', background: C.green,
                                        display: 'inline-block', boxShadow: `0 0 8px ${C.green}`
                                    }} />
                                <span style={{
                                    fontSize: '0.68rem', color: C.sub, fontWeight: 600,
                                    letterSpacing: '0.06em'
                                }}>Showroom Open · Mon–Sat 10am–7pm</span>
                            </div>
                            <h3 style={{
                                fontSize: 'clamp(1.6rem,3vw,2.5rem)', fontWeight: 600, color: C.text,
                                lineHeight: 1.15, margin: '0 0 18px', letterSpacing: '-0.03em'
                            }}>
                                See. Touch. Ride.<br />
                                <span style={{ color: C.sub, fontWeight: 400 }}>Then decide.</span>
                            </h3>
                            <p style={{
                                fontSize: '0.96rem', lineHeight: 1.75, color: C.sub, maxWidth: 460,
                                marginBottom: 28
                            }}>
                                Our showroom in Jhargram is designed for the modern EV buyer. No pressure, no rush —
                                just a premium space to explore, ask questions, and experience electric mobility firsthand.
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                                <Btn href="#contact" variant="primary" size="md">
                                    <MapPin style={{ width: 14, height: 14 }} /> Get Directions
                                </Btn>
                                <Btn href="tel:+918250602878" variant="ghost" size="md">
                                    <Phone style={{ width: 14, height: 14 }} /> Call Us
                                </Btn>
                            </div>

                            {/* Vector map pathway — Home → Showroom */}
                            <div style={{ marginTop: 28, borderRadius: 16, overflow: 'hidden', border: `1px solid ${C.border}`, background: C.bgAlt, padding: '16px 20px' }}>
                                <p style={{ fontSize: '0.6rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 600, marginBottom: 14 }}>Route to Showroom</p>
                                <svg viewBox="0 0 380 120" width="100%" style={{ overflow: 'visible' }} fill="none">
                                    <defs>
                                        <linearGradient id="routeGrad" x1="0" y1="0" x2="1" y2="0">
                                            <stop offset="0%" stopColor={C.accent} stopOpacity="0.4" />
                                            <stop offset="100%" stopColor={C.accent} stopOpacity="1" />
                                        </linearGradient>
                                        <filter id="glow">
                                            <feGaussianBlur stdDeviation="2" result="blur" />
                                            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                                        </filter>
                                    </defs>

                                    {/* Road grid background */}
                                    <line x1="60" y1="0" x2="60" y2="120" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                                    <line x1="160" y1="0" x2="160" y2="120" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                                    <line x1="260" y1="0" x2="260" y2="120" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                                    <line x1="0" y1="40" x2="380" y2="40" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                                    <line x1="0" y1="80" x2="380" y2="80" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />

                                    {/* Road lines */}
                                    <line x1="0" y1="60" x2="380" y2="60" stroke="rgba(255,255,255,0.06)" strokeWidth="8" strokeLinecap="round" />
                                    <line x1="110" y1="0" x2="110" y2="60" stroke="rgba(255,255,255,0.06)" strokeWidth="8" strokeLinecap="round" />
                                    <line x1="270" y1="60" x2="270" y2="120" stroke="rgba(255,255,255,0.06)" strokeWidth="8" strokeLinecap="round" />

                                    {/* Road dashes */}
                                    <line x1="0" y1="60" x2="380" y2="60" stroke="rgba(255,255,255,0.08)" strokeWidth="1" strokeDasharray="12 8" />

                                    {/* Route path */}
                                    <path d="M 30 60 L 110 60 L 110 20 L 270 20 L 270 60 L 350 60"
                                        stroke="url(#routeGrad)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                                        strokeDasharray="6 4" filter="url(#glow)" />

                                    {/* Animated route dot */}
                                    <motion.circle r="4" fill={C.accent} filter="url(#glow)"
                                        style={{ offsetPath: "path('M 30 60 L 110 60 L 110 20 L 270 20 L 270 60 L 350 60')" }}>
                                        <animateMotion dur="3s" repeatCount="indefinite" rotate="auto"
                                            path="M 30 60 L 110 60 L 110 20 L 270 20 L 270 60 L 350 60" />
                                    </motion.circle>

                                    {/* Point A — Home */}
                                    <circle cx="30" cy="60" r="10" fill={`${C.accent}18`} stroke={C.accent} strokeWidth="1.5" />
                                    <circle cx="30" cy="60" r="4" fill={C.accent} />
                                    <text x="30" y="82" textAnchor="middle" fill={C.sub} fontSize="8" fontFamily={F} fontWeight="600">HOME</text>

                                    {/* Point B — Showroom */}
                                    <circle cx="350" cy="60" r="10" fill="rgba(34,197,94,0.15)" stroke={C.green} strokeWidth="1.5" />
                                    <circle cx="350" cy="60" r="4" fill={C.green} />
                                    {/* Pin icon */}
                                    <path d="M350 48 C346 48 343 51 343 55 C343 60 350 67 350 67 C350 67 357 60 357 55 C357 51 354 48 350 48 Z"
                                        fill={`${C.green}25`} stroke={C.green} strokeWidth="1" />
                                    <circle cx="350" cy="55" r="2.5" fill={C.green} />
                                    <text x="350" y="82" textAnchor="middle" fill={C.sub} fontSize="8" fontFamily={F} fontWeight="600">SHOWROOM</text>

                                    {/* Distance label */}
                                    <rect x="155" y="8" width="70" height="18" rx="9" fill={`${C.accent}15`} stroke={`${C.accent}30`} strokeWidth="1" />
                                    <text x="190" y="21" textAnchor="middle" fill={C.accent} fontSize="8" fontFamily={F} fontWeight="600">~12 min drive</text>

                                    {/* Sankrail label */}
                                    <text x="190" y="38" textAnchor="middle" fill={C.muted} fontSize="7" fontFamily={F}>Sankrail, West Bengal</text>
                                </svg>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.accent }} />
                                        <span style={{ fontSize: '0.7rem', color: C.sub }}>Your Location</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.green }} />
                                        <span style={{ fontSize: '0.7rem', color: C.sub }}>ABHA Enterprise, KESHIAPATA</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* features */}
                        <div style={{
                            padding: '2rem 2.5rem', borderTop: `1px solid ${C.border}`,
                            background: C.bgAlt
                        }}>
                            <p style={{
                                fontSize: '0.62rem', color: C.muted, textTransform: 'uppercase',
                                letterSpacing: '0.16em', marginBottom: 20, fontWeight: 600
                            }}>What to Expect</p>
                            <Stagger stagger={0.09}>
                                {SHOWROOM_FEATS.map(f => {
                                    const Icon = f.icon;
                                    return (
                                        <SI key={f.title} y={12}>
                                            <motion.div whileHover={{ x: 4, background: C.surface }} transition={{ duration: 0.22 }}
                                                style={{
                                                    display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px',
                                                    borderRadius: 14, marginBottom: 5
                                                }}>
                                                <div style={{
                                                    width: 32, height: 32, borderRadius: 9, background: C.accentDim,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                                }}>
                                                    <Icon style={{ width: 14, height: 14, color: C.accent }} />
                                                </div>
                                                <div>
                                                    <p style={{
                                                        fontSize: '0.86rem', fontWeight: 600, color: C.text, margin: 0,
                                                        letterSpacing: '-0.01em'
                                                    }}>{f.title}</p>
                                                    <p style={{
                                                        fontSize: '0.76rem', color: C.sub, margin: '3px 0 0',
                                                        lineHeight: 1.55
                                                    }}>{f.desc}</p>
                                                </div>
                                            </motion.div>
                                        </SI>
                                    );
                                })}
                            </Stagger>
                        </div>
                    </div>
                </motion.div>
            </Reveal>
        </div>
    </section>
);

/* ─────────────────────────────────────────────
   WORKFLOW TIMELINE
───────────────────────────────────────────── */
const STEPS = [
    { n: '01', title: 'EV Arrival', icon: Package, color: '#4F8CFF', desc: 'New vehicles arrive and are logged into the digital inventory system with full documentation.' },
    { n: '02', title: 'Inventory Entry', icon: BarChart3, color: '#818cf8', desc: 'Barcode scanning and digital cataloguing ensures every unit is tracked from day one.' },
    { n: '03', title: 'Team Assignment', icon: Users, color: '#22C55E', desc: 'Tasks assigned to field teams via Android app — service, prep, and delivery roles.' },
    { n: '04', title: 'Service Preparation', icon: Wrench, color: '#38bdf8', desc: 'Pre-delivery inspection, charging, and quality checks completed before handover.' },
    { n: '05', title: 'Customer Delivery', icon: Zap, color: '#4F8CFF', desc: 'Documented handover with GST invoice, warranty registration, and demo ride.' },
    { n: '06', title: 'Support & Tracking', icon: Shield, color: '#818cf8', desc: 'Post-delivery support, service reminders, and digital tracking keep customers connected.' },
];

const WorkflowTimeline = () => {
    const sRef = useRef(null);
    useLayoutEffect(() => {
        if (!sRef.current) return;
        const ctx = gsap.context(() => {
            const line = sRef.current.querySelector('[data-tl-line]');
            const steps = sRef.current.querySelectorAll('[data-tl-step]');
            gsap.fromTo(line, { scaleY: 0 }, {
                scaleY: 1, duration: 1.4, ease: 'expo.out',
                scrollTrigger: { trigger: sRef.current, start: 'top 68%', toggleActions: 'play none none none' },
            });
            gsap.from(steps, {
                y: 36, opacity: 0, duration: 0.8, stagger: 0.13, ease: 'expo.out',
                scrollTrigger: { trigger: sRef.current, start: 'top 64%', toggleActions: 'play none none none' },
            });
        }, sRef);
        return () => ctx.revert();
    }, []);

    return (
        <section id="workflow" ref={sRef}
            style={{
                padding: 'clamp(5rem,10vw,9rem) 1.5rem', background: C.bg,
                position: 'relative', overflow: 'hidden'
            }}>
            <div style={{ maxWidth: 880, margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: 'clamp(3rem,6vw,4.5rem)' }}>
                    <Reveal y={10}>
                        <p style={{
                            fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.3em',
                            textTransform: 'uppercase', color: C.accent, marginBottom: 20
                        }}>How We Work</p>
                    </Reveal>
                    <SplitReveal as="h2" stagger={0.07}
                        style={{
                            fontFamily: F, fontSize: 'clamp(2rem,4.5vw,3.8rem)', fontWeight: 600,
                            letterSpacing: '-0.035em', color: C.text, margin: 0, lineHeight: 1.08
                        }}>
                        From Arrival to Your Hands.
                    </SplitReveal>
                </div>

                <div style={{ position: 'relative' }}>
                    {/* vertical line */}
                    <div data-tl-line style={{
                        position: 'absolute', left: 30, top: 0, bottom: 0, width: 1,
                        background: `linear-gradient(180deg,transparent,${C.accent},${C.border},transparent)`,
                        transformOrigin: 'top'
                    }} />

                    {STEPS.map(s => {
                        const Icon = s.icon;
                        return (
                            <div key={s.n} data-tl-step
                                style={{ display: 'flex', alignItems: 'flex-start', gap: 22, marginBottom: 16 }}>
                                <div style={{ position: 'relative', flexShrink: 0 }}>
                                    <motion.div whileHover={{ scale: 1.08 }} transition={SPRING}
                                        style={{
                                            width: 60, height: 60, borderRadius: 18, display: 'flex',
                                            alignItems: 'center', justifyContent: 'center', background: C.surface,
                                            border: `1px solid ${s.color}30`,
                                            boxShadow: `0 6px 18px ${s.color}15`
                                        }}>
                                        <Icon style={{ width: 22, height: 22, color: s.color }} />
                                    </motion.div>
                                    {/* dot on line */}
                                    <motion.div animate={{ scale: [1, 1.35, 1], opacity: [0.6, 1, 0.6] }}
                                        transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
                                        style={{
                                            position: 'absolute', left: -18, top: '50%', transform: 'translateY(-50%)',
                                            width: 8, height: 8, borderRadius: '50%', background: s.color,
                                            boxShadow: `0 0 10px ${s.color}`
                                        }} />
                                </div>
                                <div style={{ flex: 1, paddingBottom: 8 }}>
                                    <motion.div whileHover={{ x: 5, background: C.surface, borderColor: `${s.color}25` }}
                                        transition={{ duration: 0.22 }}
                                        style={{ padding: '18px 22px', borderRadius: 18, border: `1px solid ${C.border}` }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                                            <span style={{
                                                fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.2em',
                                                color: s.color
                                            }}>STEP {s.n}</span>
                                            <div style={{
                                                flex: 1, height: 1,
                                                background: `linear-gradient(90deg,${s.color}35,transparent)`
                                            }} />
                                        </div>
                                        <h3 style={{
                                            fontSize: '1rem', fontWeight: 600, color: C.text, margin: '0 0 8px',
                                            letterSpacing: '-0.015em'
                                        }}>{s.title}</h3>
                                        <p style={{ fontSize: '0.84rem', lineHeight: 1.65, color: C.sub, margin: 0 }}>{s.desc}</p>
                                    </motion.div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

/* ─────────────────────────────────────────────
   STATISTICS
───────────────────────────────────────────── */
const Statistics = () => {
    const sRef = useRef(null);
    useLayoutEffect(() => {
        if (!sRef.current) return;
        const ctx = gsap.context(() => {
            sRef.current.querySelectorAll('[data-sv]').forEach(el => {
                const target = parseFloat(el.dataset.sv);
                const suffix = el.dataset.ss || '';
                if (isNaN(target)) return;
                ScrollTrigger.create({
                    trigger: el, start: 'top 88%', once: true,
                    onEnter: () => anime({
                        targets: { v: 0 }, v: target, duration: 1800,
                        easing: 'easeOutExpo', round: target < 10 ? 10 : 1,
                        update: a => { el.innerHTML = parseFloat(a.animations[0].currentValue).toLocaleString() + suffix; }
                    })
                });
            });
        }, sRef);
        return () => ctx.revert();
    }, []);

    return (
        <section ref={sRef}
            style={{
                padding: 'clamp(4rem,8vw,7rem) 1.5rem', position: 'relative',
                overflow: 'hidden', background: C.bgAlt
            }}>
            {/* static decorative ring — no animation */}
            <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)', width: 560, height: 560, borderRadius: '50%',
                border: `1px solid ${C.border}`, pointerEvents: 'none', opacity: 0.4
            }} />

            <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
                <Stagger stagger={0.1}
                    style={{
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
                        gap: '2rem', textAlign: 'center'
                    }}>
                    {[
                        { value: 500, suffix: '+', label: 'EVs Delivered', sub: 'and counting', color: C.accent },
                        { value: 5, suffix: '+', label: 'Years in Business', sub: 'trusted since 2019', color: C.green },
                        { value: 15, suffix: '+', label: 'EV Brands', sub: 'authorised dealer', color: '#818cf8' },
                        { value: 98, suffix: '%', label: 'Customer Satisfaction', sub: 'based on reviews', color: '#38bdf8' },
                    ].map(s => (
                        <SI key={s.label} y={20}>
                            <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.28, ease: EXPO }}>
                                <p data-sv={s.value} data-ss={s.suffix}
                                    style={{
                                        fontSize: 'clamp(2.5rem,5vw,4rem)', fontWeight: 700,
                                        letterSpacing: '-0.04em', color: s.color, margin: '0 0 6px', lineHeight: 1
                                    }}>
                                    0{s.suffix}
                                </p>
                                <p style={{
                                    fontSize: '0.92rem', fontWeight: 600, color: C.text,
                                    margin: '0 0 4px'
                                }}>{s.label}</p>
                                <p style={{ fontSize: '0.76rem', color: C.muted, margin: 0 }}>{s.sub}</p>
                            </motion.div>
                        </SI>
                    ))}
                </Stagger>
            </div>
        </section>
    );
};

/* ─────────────────────────────────────────────
   CONTACT
───────────────────────────────────────────── */
const Contact = () => {
    const [bookingForm, setBookingForm] = useState(null);
    const [ticketForm, setTicketForm] = useState(null);
    const [loading, setLoading] = useState({});
    const [success, setSuccess] = useState(null);
    
    const toast = (msg, type) => {
        const el = document.createElement('div');
        el.textContent = msg;
        el.style.cssText = `
            position: fixed; bottom: 24px; right: 24px; padding: 16px 24px; border-radius: 12px;
            background: ${type === 'success' ? '#22c55e' : '#ef4444'}; color: white;
            font-weight: 500; z-index: 9999; box-shadow: 0 10px 40px rgba(0,0,0,.3);
        `;
        document.body.appendChild(el);
        setTimeout(() => el.remove(), 3000);
    };

    const handleBookingSubmit = async (e) => {
        e.preventDefault();
        if (!bookingForm?.customer_name || !bookingForm?.phone_number || !bookingForm?.brand || !bookingForm?.model) {
            toast('Please fill all required fields', 'error'); return;
        }
        setLoading({ ...loading, booking: true });
        try {
            const data = await createBooking({
                customer_name: bookingForm.customer_name,
                phone_number: bookingForm.phone_number,
                address: bookingForm.address || '',
                brand: bookingForm.brand,
                model: bookingForm.model,
                variant: bookingForm.variant,
                colour: bookingForm.colour,
                booking_amount: bookingForm.booking_amount ? parseFloat(bookingForm.booking_amount) : null,
                payment_type: bookingForm.payment_type,
                customer_type: bookingForm.customer_type || 'TEST_RIDE',
            });
            setSuccess({ type: 'booking', id: data.id });
            setBookingForm(null);
            toast('Booking submitted! We will contact you shortly', 'success');
        } catch (err) {
            toast(err.message || 'Submission failed', 'error');
        } finally {
            setLoading({ ...loading, booking: false });
        }
    };

    const handleTicketSubmit = async (e) => {
        e.preventDefault();
        if (!ticketForm?.title || !ticketForm?.customer_name || !ticketForm?.phone_number) {
            toast('Please fill all required fields', 'error'); return;
        }
        setLoading({ ...loading, ticket: true });
        try {
            const data = await createTicket({
                title: ticketForm.title,
                description: ticketForm.description || '',
                customer_name: ticketForm.customer_name,
                phone_number: ticketForm.phone_number,
                address: ticketForm.address || '',
                category: ticketForm.category || 'COMPLAINT',
                priority: ticketForm.priority || 'MEDIUM',
            });
            setSuccess({ type: 'ticket', id: data.id, title: data.title });
            setTicketForm(null);
            toast(`Ticket #${data.id} created. We will resolve soon`, 'success');
        } catch (err) {
            toast(err.message || 'Ticket creation failed', 'error');
        } finally {
            setLoading({ ...loading, ticket: false });
        }
    };

    const EV_MODELS = ['Ola S1 Pro', 'TVS iQube', 'Zelio Eeva', 'Ampere Primus', 'Ather Rizta'];
    const CATEGORIES = ['COMPLAINT', 'INQUIRY', 'SERVICE', 'WARRANTY', 'GENERAL'];
    const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH'];

    return (
        <section id="contact"
            style={{
                padding: 'clamp(5rem,10vw,9rem) 1.5rem', position: 'relative',
                overflow: 'hidden', background: C.bg
            }}>
        <div style={{
            position: 'absolute', inset: 0,
            background: `radial-gradient(ellipse at 50% 100%,${C.accentDim} 0%,transparent 55%)`,
            pointerEvents: 'none'
        }} />

        <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
                gap: 'clamp(2.5rem,6vw,5rem)', alignItems: 'start'
            }}>

                {/* Left */}
                <div>
                    <Reveal y={10}>
                        <p style={{
                            fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.3em',
                            textTransform: 'uppercase', color: C.accent, marginBottom: 18
                        }}>Get In Touch</p>
                    </Reveal>
                    <SplitReveal as="h2" stagger={0.07}
                        style={{
                            fontFamily: F, fontSize: 'clamp(1.8rem,3.8vw,3.2rem)', fontWeight: 600,
                            letterSpacing: '-0.035em', color: C.text, margin: '0 0 20px', lineHeight: 1.1
                        }}>
                        Ready to Go Electric?
                    </SplitReveal>
                    <Reveal delay={0.18} y={14}>
                        <p style={{ fontSize: '1rem', lineHeight: 1.75, color: C.sub, maxWidth: 440, marginBottom: 32 }}>
                            Visit our showroom, book a test ride, or reach out to our team.
                            We're here to help you make the switch — on your terms.
                        </p>
                    </Reveal>
                    <Stagger stagger={0.08} delay={0.2}>
                        {[
                            { icon: MapPin, label: 'Showroom Address', value: 'ABHA Enterprise, KESHIAPATA, Sankrail, West Bengal 721135', color: C.accent },
                            { icon: Phone, label: 'Call Us', value: '+91 82506 02878', color: C.green },
                            { icon: Mail, label: 'Email Us', value: 'abhaenterprise2020@gmail.com', color: '#818cf8' },
                            { icon: Clock, label: 'Working Hours', value: 'Mon–Sat: 10:00 AM – 7:00 PM', color: '#38bdf8' },
                        ].map(item => {
                            const Icon = item.icon;
                            return (
                                <SI key={item.label} y={14}>
                                    <motion.div whileHover={{ x: 5, background: C.surface, borderColor: `${item.color}25` }}
                                        transition={{ duration: 0.22 }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
                                            borderRadius: 16, marginBottom: 8, border: `1px solid ${C.border}`
                                        }}>
                                        <div style={{
                                            width: 38, height: 38, borderRadius: 11, flexShrink: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: `${item.color}12`, border: `1px solid ${item.color}22`
                                        }}>
                                            <Icon style={{ width: 16, height: 16, color: item.color }} />
                                        </div>
                                        <div>
                                            <p style={{
                                                fontSize: '0.6rem', color: C.muted, textTransform: 'uppercase',
                                                letterSpacing: '0.12em', margin: 0, fontWeight: 600
                                            }}>{item.label}</p>
                                            <p style={{
                                                fontSize: '0.88rem', fontWeight: 500, color: C.text,
                                                margin: '3px 0 0', letterSpacing: '-0.01em'
                                            }}>{item.value}</p>
                                        </div>
                                    </motion.div>
                                </SI>
                            );
                        })}
                    </Stagger>
                </div>

                {/* Form */}
                <Reveal delay={0.14} y={36}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        {/* Booking Form */}
                        <motion.div whileHover={{ borderColor: C.borderHov }} transition={{ duration: 0.3 }}
                            style={{
                                padding: '2.5rem', borderRadius: 24, position: 'relative', overflow: 'hidden',
                                background: C.surface, border: `1px solid ${C.border}`,
                                boxShadow: '0 24px 60px rgba(0,0,0,.3)'
                            }}>
                            <div style={{
                                position: 'absolute', inset: '0 0 auto', height: 1,
                                background: `linear-gradient(90deg,transparent,${C.accent},transparent)`
                            }} />
                            <h3 style={{
                                fontSize: '1.25rem', fontWeight: 700, color: C.text, margin: '0 0 20px',
                                letterSpacing: '-0.02em'
                            }}>Book {success?.type === 'booking' && success ? 'Another' : ''} EV</h3>
                            <form onSubmit={handleBookingSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div>
                                    <label style={{
                                        display: 'block', fontSize: '0.6rem', color: C.muted,
                                        textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 8,
                                        fontWeight: 600
                                    }}>Type</label>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        {['Test Ride', 'Dealer'].map(t => (
                                            <button
                                                key={t}
                                                type="button"
                                                onClick={() => setBookingForm(f => ({ ...f, customer_type: t.replace(' ', '_').toUpperCase() }))}
                                                style={{
                                                    flex: 1, padding: '10px 8px', borderRadius: 10, border: '1px solid',
                                                    background: bookingForm?.customer_type === t.replace(' ', '_').toUpperCase() ? C.accent : 'transparent',
                                                    color: bookingForm?.customer_type === t.replace(' ', '_').toUpperCase() ? '#fff' : C.sub,
                                                    borderColor: bookingForm?.customer_type === t.replace(' ', '_').toUpperCase() ? C.accent : C.border,
                                                    cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500,
                                                    transition: 'all .15s'
                                                }}
                                            >
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {[
                                    { label: 'Name', placeholder: 'Rahul Sharma', type: 'text', field: 'customer_name' },
                                    { label: 'Phone', placeholder: '+91 98765 43210', type: 'tel', field: 'phone_number' },
                                    { label: 'Address', placeholder: 'Your address', type: 'text', field: 'address' },
                                ].map(f => (
                                    <div key={f.label}>
                                        <label style={{
                                            display: 'block', fontSize: '0.6rem', color: C.muted,
                                            textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 8,
                                            fontWeight: 600
                                        }}>{f.label}</label>
                                        <input
                                            type={f.type}
                                            value={bookingForm?.[f.field] || ''}
                                            onChange={e => setBookingForm(f2 => ({ ...f2, [f.field]: e.target.value }))}
                                            placeholder={f.placeholder}
                                            style={{
                                                width: '100%', padding: '12px 16px', borderRadius: 12,
                                                fontSize: '0.88rem', color: C.text, background: C.bgAlt,
                                                border: `1px solid ${C.border}`, outline: 'none', fontFamily: F,
                                                transition: 'border-color .2s, box-shadow .2s'
                                            }}
                                            onFocus={e => { e.target.style.borderColor = C.accent; e.target.style.boxShadow = `0 0 0 3px ${C.accentDim}`; }}
                                            onBlur={e => { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; }}
                                            required
                                        />
                                    </div>
                                ))}
                                <div>
                                    <label style={{
                                        display: 'block', fontSize: '0.6rem', color: C.muted,
                                        textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 8,
                                        fontWeight: 600
                                    }}>Brand</label>
                                    <select
                                        value={bookingForm?.brand || ''}
                                        onChange={e => setBookingForm(f => ({ ...f, brand: e.target.value, model: '', variant: '', colour: '' }))}
                                        style={{
                                            width: '100%', padding: '12px 16px', borderRadius: 12,
                                            fontSize: '0.88rem', color: C.text, background: C.bgAlt,
                                            border: `1px solid ${C.border}`, outline: 'none', fontFamily: F,
                                            appearance: 'none', colorScheme: 'dark'
                                        }}
                                        required
                                    >
                                        <option value="">Select brand...</option>
                                        <option value="Mahindra">Mahindra</option>
                                        <option value="Ola">Ola</option>
                                        <option value="Ather">Ather</option>
                                        <option value="Ampere">Ampere</option>
                                        <option value="TVS">TVS</option>
                                        <option value="Zelio">Zelio</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{
                                        display: 'block', fontSize: '0.6rem', color: C.muted,
                                        textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 8,
                                        fontWeight: 600
                                    }}>Model</label>
                                    <select
                                        value={bookingForm?.model || ''}
                                        onChange={e => setBookingForm(f => ({ ...f, model: e.target.value }))}
                                        style={{
                                            width: '100%', padding: '12px 16px', borderRadius: 12,
                                            fontSize: '0.88rem', color: C.text, background: C.bgAlt,
                                            border: `1px solid ${C.border}`, outline: 'none', fontFamily: F,
                                            appearance: 'none', colorScheme: 'dark'
                                        }}
                                        required
                                    >
                                        <option value="">Select model...</option>
                                        {EV_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <label style={{
                                            display: 'block', fontSize: '0.6rem', color: C.muted,
                                            textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 8,
                                            fontWeight: 600
                                        }}>Colour</label>
                                        <input
                                            value={bookingForm?.colour || ''}
                                            onChange={e => setBookingForm(f => ({ ...f, colour: e.target.value }))}
                                            placeholder="e.g. Matte Black"
                                            style={{
                                                width: '100%', padding: '12px 16px', borderRadius: 12,
                                                fontSize: '0.88rem', color: C.text, background: C.bgAlt,
                                                border: `1px solid ${C.border}`, outline: 'none', fontFamily: F
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{
                                            display: 'block', fontSize: '0.6rem', color: C.muted,
                                            textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 8,
                                            fontWeight: 600
                                        }}>Booking Amount</label>
                                        <input
                                            type="number"
                                            value={bookingForm?.booking_amount || ''}
                                            onChange={e => setBookingForm(f => ({ ...f, booking_amount: e.target.value }))}
                                            placeholder="e.g. 50000"
                                            style={{
                                                width: '100%', padding: '12px 16px', borderRadius: 12,
                                                fontSize: '0.88rem', color: C.text, background: C.bgAlt,
                                                border: `1px solid ${C.border}`, outline: 'none', fontFamily: F
                                            }}
                                        />
                                    </div>
                                </div>
                                <Btn
                                    variant="primary" size="md" fullWidth
                                    onClick={(e) => e.preventDefault()}
                                    type="submit"
                                    disabled={loading.booking}
                                    style={{ cursor: loading.booking ? 'not-allowed' : undefined }}
                                >
                                    {loading.booking ? 'Booking...' : <><Zap style={{ width: 15, height: 15 }} /> Book Now</>}
                                </Btn>
                                <p style={{ fontSize: '0.74rem', textAlign: 'center', color: C.muted, margin: 0 }}>
                                    We'll contact you within 24 hours to confirm.
                                </p>
                            </form>
                        </motion.div>

                        {/* Ticket Form */}
                        <motion.div whileHover={{ borderColor: C.borderHov }} transition={{ duration: 0.3 }}
                            style={{
                                padding: '2.5rem', borderRadius: 24, position: 'relative', overflow: 'hidden',
                                background: C.surface, border: `1px solid ${C.border}`,
                                boxShadow: '0 24px 60px rgba(0,0,0,.3)'
                            }}>
                            <div style={{
                                position: 'absolute', inset: '0 0 auto', height: 1,
                                background: `linear-gradient(90deg,transparent,${C.green},transparent)`
                            }} />
                            <h3 style={{
                                fontSize: '1.25rem', fontWeight: 700, color: C.text, margin: '0 0 20px',
                                letterSpacing: '-0.02em'
                            }}>Raise a Complaint Ticket</h3>
                            <form onSubmit={handleTicketSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {[
                                    { label: 'Name', placeholder: 'Your Name', type: 'text', field: 'customer_name' },
                                    { label: 'Phone', placeholder: '+91 98765 43210', type: 'tel', field: 'phone_number' },
                                    { label: 'Issue Title', placeholder: 'Brief issue description', type: 'text', field: 'title' },
                                    { label: 'Details', placeholder: 'Describe your issue in detail', type: 'textarea', field: 'description' },
                                ].map(f => (
                                    <div key={f.label}>
                                        <label style={{
                                            display: 'block', fontSize: '0.6rem', color: C.muted,
                                            textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 8,
                                            fontWeight: 600
                                        }}>{f.label}</label>
                                        {f.type === 'textarea' ? (
                                            <textarea
                                                value={ticketForm?.[f.field] || ''}
                                                onChange={e => setTicketForm(f2 => ({ ...f2, [f.field]: e.target.value }))}
                                                placeholder={f.placeholder}
                                                rows={3}
                                                style={{
                                                    width: '100%', padding: '12px 16px', borderRadius: 12,
                                                    fontSize: '0.88rem', color: C.text, background: C.bgAlt,
                                                    border: `1px solid ${C.border}`, outline: 'none', fontFamily: F,
                                                    resize: 'vertical'
                                                }}
                                                required
                                            />
                                        ) : (
                                            <input
                                                type={f.type}
                                                value={ticketForm?.[f.field] || ''}
                                                onChange={e => setTicketForm(f2 => ({ ...f2, [f.field]: e.target.value }))}
                                                placeholder={f.placeholder}
                                                style={{
                                                    width: '100%', padding: '12px 16px', borderRadius: 12,
                                                    fontSize: '0.88rem', color: C.text, background: C.bgAlt,
                                                    border: `1px solid ${C.border}`, outline: 'none', fontFamily: F
                                                }}
                                                required
                                            />
                                        )}
                                    </div>
                                ))}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <label style={{
                                            display: 'block', fontSize: '0.6rem', color: C.muted,
                                            textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 8,
                                            fontWeight: 600
                                        }}>Category</label>
                                        <select
                                            value={ticketForm?.category || 'COMPLAINT'}
                                            onChange={e => setTicketForm(f => ({ ...f, category: e.target.value }))}
                                            style={{
                                                width: '100%', padding: '12px 16px', borderRadius: 12,
                                                fontSize: '0.88rem', color: C.text, background: C.bgAlt,
                                                border: `1px solid ${C.border}`, outline: 'none', fontFamily: F,
                                                appearance: 'none', colorScheme: 'dark'
                                            }}
                                        >
                                            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{
                                            display: 'block', fontSize: '0.6rem', color: C.muted,
                                            textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 8,
                                            fontWeight: 600
                                        }}>Priority</label>
                                        <select
                                            value={ticketForm?.priority || 'MEDIUM'}
                                            onChange={e => setTicketForm(f => ({ ...f, priority: e.target.value }))}
                                            style={{
                                                width: '100%', padding: '12px 16px', borderRadius: 12,
                                                fontSize: '0.88rem', color: C.text, background: C.bgAlt,
                                                border: `1px solid ${C.border}`, outline: 'none', fontFamily: F,
                                                appearance: 'none', colorScheme: 'dark'
                                            }}
                                        >
                                            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <Btn
                                    variant="primary" size="md" fullWidth
                                    onClick={(e) => e.preventDefault()}
                                    type="submit"
                                    disabled={loading.ticket}
                                    style={{ cursor: loading.ticket ? 'not-allowed' : undefined }}
                                >
                                    {loading.ticket ? 'Creating...' : <><Ticket size={15} /> Create Ticket</>}
                                </Btn>
                                <p style={{ fontSize: '0.74rem', textAlign: 'center', color: C.muted, margin: 0 }}>
                                    You will receive a ticket ID for tracking.
                                </p>
                            </form>
                        </motion.div>
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
    );
};

/* ─────────────────────────────────────────────
   FOOTER
───────────────────────────────────────────── */
const Footer = () => (
    <footer style={{
        padding: '4rem 1.5rem 2.5rem', background: C.bgAlt,
        borderTop: `1px solid ${C.border}`
    }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
                gap: '2.5rem', marginBottom: '2.5rem'
            }}>
                {/* brand */}
                <div style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10, overflow: 'hidden',
                            border: `1px solid ${C.border}`, flexShrink: 0
                        }}>
                            <img src="/abha-logo.png" alt="Abha Enterprise"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <div>
                            <p style={{
                                fontSize: '0.9rem', fontWeight: 600, color: C.text, margin: 0,
                                letterSpacing: '-0.015em'
                            }}>Abha Enterprise</p>
                            <p style={{
                                fontSize: '0.52rem', color: C.muted, letterSpacing: '0.16em',
                                textTransform: 'uppercase', margin: '3px 0 0', fontWeight: 500
                            }}>EV Mobility · Sankrail, WB</p>
                        </div>
                    </div>
                    <p style={{ fontSize: '0.82rem', lineHeight: 1.7, color: C.muted, maxWidth: 300, marginBottom: 16 }}>
                        Authorised multi-brand EV dealership in Sankrail, West Bengal — bringing premium electric mobility with digital-first operations.
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <motion.span animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.3, 1] }}
                            transition={{ duration: 2.2, repeat: Infinity }}
                            style={{
                                width: 6, height: 6, borderRadius: '50%', background: C.green,
                                display: 'inline-block'
                            }} />
                        <span style={{ fontSize: '0.7rem', color: C.green, fontWeight: 500 }}>Showroom Open Today</span>
                    </div>
                </div>

                {/* nav */}
                <div>
                    <h4 style={{
                        fontSize: '0.62rem', fontWeight: 600, color: C.text, textTransform: 'uppercase',
                        letterSpacing: '0.16em', marginBottom: 20
                    }}>Explore</h4>
                    <ul style={{
                        listStyle: 'none', padding: 0, margin: 0, display: 'flex',
                        flexDirection: 'column', gap: 10
                    }}>
                        {NAV.map(l => (
                            <li key={l.label}>
                                <motion.a href={l.href} whileHover={{ x: 3, color: C.accent }} transition={{ duration: 0.18 }}
                                    style={{
                                        fontSize: '0.82rem', color: C.muted, textDecoration: 'none',
                                        display: 'inline-block'
                                    }}>
                                    {l.label}
                                </motion.a>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* brands */}
                <div>
                    <h4 style={{
                        fontSize: '0.62rem', fontWeight: 600, color: C.text, textTransform: 'uppercase',
                        letterSpacing: '0.16em', marginBottom: 20
                    }}>Our Brands</h4>
                    <ul style={{
                        listStyle: 'none', padding: 0, margin: 0, display: 'flex',
                        flexDirection: 'column', gap: 10
                    }}>
                        {['Ola Electric', 'TVS', 'Zelio', 'Ampere', 'Ather'].map(b => (
                            <li key={b}>
                                <span style={{ fontSize: '0.82rem', color: C.muted }}>{b}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* bottom bar */}
            <div style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                justifyContent: 'space-between', gap: 12, paddingTop: '1.5rem',
                borderTop: `1px solid ${C.border}`
            }}>
                <p style={{ fontSize: '0.74rem', color: C.muted, margin: 0 }}>
                    © 2026 Abha Enterprise. All rights reserved.
                </p>
                <div style={{ display: 'flex', gap: 20 }}>
                    {['Privacy Policy', 'Terms of Service'].map(l => (
                        <motion.a key={l} href="#" whileHover={{ color: C.sub }} transition={{ duration: 0.18 }}
                            style={{ fontSize: '0.74rem', color: C.muted, textDecoration: 'none' }}>
                            {l}
                        </motion.a>
                    ))}
                </div>
                <p style={{
                    fontSize: '0.74rem', color: C.muted, margin: 0,
                    display: 'flex', alignItems: 'center', gap: 5
                }}>
                    Powered by Falcon Engine ❤️
                </p>
            </div>
        </div>
    </footer>
);

/* ─────────────────────────────────────────────
   ROOT
───────────────────────────────────────────── */
const Landing = () => {
    useSmoothScroll();

    useEffect(() => {
        const t = setTimeout(() => ScrollTrigger.refresh(), 250);
        return () => clearTimeout(t);
    }, []);

    return (
        <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: F }}>
            <style>{GLOBAL_CSS}</style>
            <Navbar />
            <Hero />
            <WhyElectric />
            <About />
            <EVCollection />
            <SmartOperations />
            <Showroom />
            <WorkflowTimeline />
            <Statistics />
            <Contact />
            <Footer />
        </div>
    );
};

export default Landing;
